import { useEffect, useMemo, useRef, useState } from "react";
import {
	useAccount,
	useConnectUI,
	useCurrentConnector,
	useDisconnect,
	useWallet,
} from "@fuels/react";
import { Address, Contract, getMintedAssetId, Provider, sha256 } from "fuels";
import deployments from "../../config/deployments.json";
import vaultAbi from "../../contracts/vault/out/debug/fuel_earn_vault-abi.json";
import marketAbi from "../../contracts/bond-market/out/debug/fuel_earn_bond_market-abi.json";
import marketV2Abi from "../../contracts/bond-market-v2/out/debug/fuel_earn_bond_market_v2-abi.json";
import testAssetsAbi from "./test-assets-abi.json";
import twapOracleAbi from "../../contracts/simplified-twap-oracle/abi.json";
import o2OracleAbi from "../../contracts/o2-price-feed/abi.json";
import proxyAbi from "../../contracts/owned-proxy/out/debug/fuel_earn_owned_proxy-abi.json";
import {
	assetRawAmount,
	balancePresets,
	DAY,
	decimalAmount,
	decimalRawAmount,
	earnAmount,
	hasMeaningfulReward,
	marketFee,
	nonnegativeFinite,
	penaltyBps,
	projectedBondValue,
	rewardBalances,
	sanitizeAmountInput,
	TERMS,
	type TermMonths,
	yieldUsdc,
} from "./economics";
import { hasBothTestAssets } from "./faucet";
import { cachedOraclePrice, type OraclePrice } from "./oracle-display";
import {
	depositSuccess,
	readDepositSuccess,
	type DepositSuccess,
} from "./deposit-success";

type Asset = "FUEL" | "stFUEL";
type Position = {
	id: string;
	owner: string;
	asset: Asset;
	amount: string;
	openingPrice: string;
	promisedUsdc: string;
	openedAt: number;
	maturity: number;
	termMonths: TermMonths;
	status: string;
	listing?: {
		id: string;
		quoteAsset: "ETH" | "FUEL" | "USDC";
		price: string;
		expiresAt?: number | null;
		feeBps?: number;
		legacy?: boolean;
	};
};
type Bid = {
	id: string;
	bidder: string;
	budget: string;
	remaining: string;
	pricePerBond: string;
	collateral: Asset | null;
	termMonths: TermMonths | null;
	minRemainingSeconds: number;
	maxRemainingSeconds: number | null;
	minCollateral: string | null;
	maxCollateral: string | null;
	minPromisedUsdc: string | null;
	maxPromisedUsdc: string | null;
	filledCount: number;
	feeBps: number;
	expiresAt: number;
	status: string;
};
type Stats = {
	tvlUsd: string;
	fuelLocked: string;
	stFuelLocked: string;
	promisedUsdc: string;
	reserveUsdc: string;
	coverageBps: number;
	activePositions: number;
	activeOrders: number;
	bondsIssued: number;
	bondsOutstanding: number;
	bondsMatured: number;
	bondsSettled: number;
	bondsEarlyExited: number;
	bonds6Months: number;
	bonds9Months: number;
	bonds12Months: number;
	issuanceVolumeUsdc: string;
	fixedReturnsIssuedUsdc: string;
	asksPosted: number;
	ordersFilled: number;
	ordersCancelled: number;
	fillRateBps: number;
	uniqueSellers: number;
	uniqueBuyers: number;
	averageFillSeconds: number;
	salesVolumeUsdc: string;
	salesVolumeFuel: string;
	asks24h: number;
	fills24h: number;
	cancellations24h: number;
	volumeUsdc24h: string;
	volumeFuel24h: string;
	accruedFeesFuel: string;
	accruedFeesUsdc: string;
	indexedAt?: string;
	blockHeight?: number;
	reconciliationStatus?: string;
};

const API =
	location.hostname === "localhost"
		? "http://localhost:8787"
		: "https://fuel-vault-api.nick-j-alexander.workers.dev";
const ZERO_STATS: Stats = {
	tvlUsd: "0",
	fuelLocked: "0",
	stFuelLocked: "0",
	promisedUsdc: "0",
	reserveUsdc: "0",
	coverageBps: 0,
	activePositions: 0,
	activeOrders: 0,
	bondsIssued: 0,
	bondsOutstanding: 0,
	bondsMatured: 0,
	bondsSettled: 0,
	bondsEarlyExited: 0,
	bonds6Months: 0,
	bonds9Months: 0,
	bonds12Months: 0,
	issuanceVolumeUsdc: "0",
	fixedReturnsIssuedUsdc: "0",
	asksPosted: 0,
	ordersFilled: 0,
	ordersCancelled: 0,
	fillRateBps: 0,
	uniqueSellers: 0,
	uniqueBuyers: 0,
	averageFillSeconds: 0,
	salesVolumeUsdc: "0",
	salesVolumeFuel: "0",
	asks24h: 0,
	fills24h: 0,
	cancellations24h: 0,
	volumeUsdc24h: "0",
	volumeFuel24h: "0",
	accruedFeesFuel: "0",
	accruedFeesUsdc: "0",
};
const links = [
	["/market", "Bonds"],
	["/stats", "Stats"],
	["https://docs.fuel.place", "Docs"],
] as const;
const MARKET_V2 = deployments.testnet as typeof deployments.testnet & {
	ethAssetId: string;
	legacyMarketContractId: string;
	marketImplementationContractId: string;
};
const TESTNET_FAUCET = "https://faucet-testnet.fuel.network/";
const TEST_ASSETS_CONTRACT = deployments.testnet.testAssetsContractId;
const DEPOSIT_SUCCESS_KEY = "fuel-latest-deposit-success";
const TELEMETRY =
	"https://fuel-vault-reporter.nick-j-alexander.workers.dev/v1/event";

type TelemetryEvent =
	| "page_view"
	| "wallet_connect"
	| "wallet_disconnect"
	| "api_request"
	| "frontend_error"
	| "transaction";

function telemetry(
	event: TelemetryEvent,
	status: "attempt" | "success" | "failure",
) {
	if (!["fuel.place", "fuel-vault.pages.dev"].includes(location.hostname))
		return;
	const body = JSON.stringify({
		event,
		status,
		network: "testnet",
		page: location.pathname,
	});
	if (
		navigator.sendBeacon?.(TELEMETRY, new Blob([body], { type: "text/plain" }))
	)
		return;
	void fetch(TELEMETRY, { method: "POST", body, keepalive: true }).catch(
		() => {},
	);
}

async function connectTracked(connect: () => unknown) {
	telemetry("wallet_connect", "attempt");
	try {
		await connect();
	} catch {
		telemetry("wallet_connect", "failure");
	}
}

function latestDepositSuccess() {
	try {
		return readDepositSuccess(sessionStorage.getItem(DEPOSIT_SUCCESS_KEY));
	} catch {
		return null;
	}
}

function faucetUrl(account: string) {
	const url = new URL(TESTNET_FAUCET);
	url.searchParams.set("address", account);
	url.searchParams.set("redirectUrl", location.origin);
	url.searchParams.set("autoClose", "true");
	return url.toString();
}

function money(value: number | string) {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 2,
	}).format(Math.max(0, Number(value) || 0));
}
function number(value: number | string, digits = 2) {
	return new Intl.NumberFormat("en-US", {
		maximumFractionDigits: digits,
	}).format(Math.max(0, Number(value) || 0));
}
function walletBalance(raw: bigint, decimals: number) {
	const [whole, fraction = ""] = decimalAmount(raw, decimals).split(".");
	const visible = fraction.slice(0, 6).replace(/0+$/, "");
	return `${BigInt(whole).toLocaleString("en-US")}${visible ? `.${visible}` : ""}`;
}
function formatAmountInput(value: string) {
	if (!value) return "";
	const [whole, fraction] = value.split(".");
	const formatted = new Intl.NumberFormat("en-US", {
		maximumFractionDigits: 0,
	}).format(Number(whole) || 0);
	return fraction === undefined ? formatted : `${formatted}.${fraction.slice(0, 2)}`;
}
function short(value?: string | null, head = 8, tail = 6) {
	return value ? `${value.slice(0, head)}…${value.slice(-tail)}` : "";
}
function date(seconds: number) {
	return new Date(seconds * 1_000).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}
function remaining(seconds: number) {
	const days = Math.max(0, Math.ceil((seconds - Date.now() / 1_000) / DAY));
	return days ? `${days} days` : "Matured";
}
function duration(seconds: number) {
	if (!seconds) return "—";
	if (seconds < 60) return `${seconds}s`;
	if (seconds < 3_600) return `${number(seconds / 60, 0)}m`;
	if (seconds < DAY) return `${number(seconds / 3_600, 1)}h`;
	return `${number(seconds / DAY, 1)}d`;
}

function unixTime(value: unknown) {
	return Number(
		BigInt(String(value).replace(/\.0+$/, "")) - 4_611_686_018_427_387_914n,
	);
}

async function getJson<T>(path: string): Promise<T> {
	try {
		const response = await fetch(`${API}${path}`);
		if (!response.ok) throw new Error(`API returned ${response.status}`);
		const body = (await response.json()) as T;
		telemetry("api_request", "success");
		return body;
	} catch (error) {
		telemetry("api_request", "failure");
		throw error;
	}
}

function lastOraclePrice(asset: Asset) {
	try {
		const key = `fuel-twap-price-${asset}`;
		const cached = cachedOraclePrice(localStorage.getItem(key));
		if (!cached) localStorage.removeItem(key);
		return cached;
	} catch {
		return null;
	}
}

function u128(value: {
	upper: { toString(): string };
	lower: { toString(): string };
}) {
	return (
		(BigInt(value.upper.toString()) << 64n) + BigInt(value.lower.toString())
	);
}

async function oraclePrice(asset: Asset): Promise<OraclePrice> {
	const key = `fuel-twap-price-${asset}`;
	const cached = lastOraclePrice(asset);
	if (cached && Date.now() - cached.cachedAt < 15_000) return cached;
	try {
		const provider = new Provider(deployments.testnet.providerUrl);
		const oracle = new Contract(
			deployments.testnet.twapOracleContractId,
			twapOracleAbi,
			provider,
		);
		const liveOracle = new Contract(
			deployments.testnet.o2PriceFeedContractId,
			o2OracleAbi,
			provider,
		);
		const [priceCall, livePriceCall, countCall] = await Promise.all([
			oracle.functions.price().get(),
			liveOracle.functions.price({ bits: deployments.testnet.o2FuelAssetId }).get(),
			oracle.functions.sample_count().get(),
		]);
		const record = priceCall.value;
		const liveRecord = livePriceCall.value;
		if (!record || !liveRecord) throw new Error("On-chain oracle price is unavailable");
		const bid = u128(record.bid);
		const ask = u128(record.ask);
		const liveBid = u128(liveRecord.bid);
		const liveAsk = u128(liveRecord.ask);
		const priceE6 = (liveBid < bid ? liveBid : bid) / 1_000_000_000_000n;
		if (
			record.asset_decimals !== 9 ||
			liveRecord.asset_decimals !== 9 ||
			bid === 0n ||
			liveBid === 0n ||
			ask < bid ||
			liveAsk < liveBid ||
			priceE6 === 0n ||
			priceE6 > 18_446_744_073_709_551_615n
		)
			throw new Error("On-chain oracle price is invalid");
		const sampleCount = Number(BigInt(countCall.value));
		const result = {
			price: Number(priceE6) / 1_000_000,
			observedAt: Math.min(Number(BigInt(record.timestamp)), Number(BigInt(liveRecord.timestamp))),
			source: `Lower of live O2 and rolling TWAP · ${sampleCount} sample${sampleCount === 1 ? "" : "s"}`,
			cachedAt: Date.now(),
			sampleCount,
		};
		try {
			localStorage.setItem(key, JSON.stringify(result));
		} catch {
			/* Display the fresh price without persistence. */
		}
		return result;
	} catch (error) {
		if (cached) return cached;
		throw error;
	}
}

function Telemetry() {
	const { account } = useAccount();
	const connected = useRef(false);
	useEffect(() => {
		telemetry("page_view", "success");
		const failed = () => telemetry("frontend_error", "failure");
		addEventListener("error", failed);
		addEventListener("unhandledrejection", failed);
		return () => {
			removeEventListener("error", failed);
			removeEventListener("unhandledrejection", failed);
		};
	}, []);
	useEffect(() => {
		if (account && !connected.current) telemetry("wallet_connect", "success");
		if (!account && connected.current)
			telemetry("wallet_disconnect", "success");
		connected.current = Boolean(account);
	}, [account]);
	return null;
}

function Header() {
	const { account } = useAccount();
	const { connect, isConnecting } = useConnectUI();
	const { disconnect } = useDisconnect();
	const [menu, setMenu] = useState(false);
	const [theme, setTheme] = useState(
		() => localStorage.getItem("theme") || "dark",
	);
	useEffect(() => {
		document.documentElement.dataset.theme = theme;
		localStorage.setItem("theme", theme);
	}, [theme]);
	useEffect(() => {
		document.body.classList.toggle("menu-open", menu);
		const close = (event: KeyboardEvent) => {
			if (event.key === "Escape") setMenu(false);
		};
		window.addEventListener("keydown", close);
		return () => {
			document.body.classList.remove("menu-open");
			window.removeEventListener("keydown", close);
		};
	}, [menu]);
	return (
		<header>
			<a className="brand" href="/" aria-label="Fuel Earn home">
				<img src="/resources/fuel-mark.svg" alt="" />
				<span>Earn</span>
			</a>
			<nav className={menu ? "open" : ""} aria-label="Primary">
				{account && (
					<a
						href="/positions"
						className={location.pathname === "/positions" ? "active" : ""}
						onClick={() => setMenu(false)}
					>
						My positions
					</a>
				)}
				{links.map(([href, label]) => (
					<a
						key={href}
						className={location.pathname === href ? "active" : ""}
						href={href}
						onClick={() => setMenu(false)}
					>
						{label}
					</a>
				))}
				<a
					className="nav-icon github-link"
					href="https://github.com/FuelLabs"
					target="_blank"
					rel="noopener noreferrer"
					aria-label="GitHub"
				>
					<svg viewBox="0 0 16 16" aria-hidden="true">
						<path d="M8 0C3.58 0 0 3.64 0 8.13c0 3.59 2.29 6.64 5.47 7.72.4.08.55-.18.55-.39 0-.19-.01-.83-.01-1.51-2.01.38-2.53-.5-2.69-.96-.09-.23-.48-.96-.82-1.15-.28-.15-.68-.52-.01-.53.63-.01 1.08.59 1.23.83.72 1.23 1.87.88 2.33.67.07-.53.28-.88.51-1.08-1.78-.21-3.64-.91-3.64-4.02 0-.89.31-1.62.82-2.19-.08-.2-.36-1.04.08-2.16 0 0 .67-.22 2.2.84A7.5 7.5 0 0 1 8 3.93c.68 0 1.36.09 2 .27 1.53-1.06 2.2-.84 2.2-.84.44 1.12.16 1.96.08 2.16.51.57.82 1.3.82 2.19 0 3.12-1.87 3.81-3.65 4.02.29.25.54.74.54 1.5 0 1.08-.01 1.95-.01 2.23 0 .21.15.47.55.39A8.14 8.14 0 0 0 16 8.13C16 3.64 12.42 0 8 0Z" />
					</svg>
					<span className="mobile-label">GitHub</span>
				</a>
				<button
					className="nav-icon theme-nav-toggle"
					onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
					aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
				>
					{theme === "dark" ? (
						<svg viewBox="0 0 24 24" aria-hidden="true">
							<path d="M12 5a1 1 0 0 1-1-1V2a1 1 0 1 1 2 0v2a1 1 0 0 1-1 1Zm0 17a1 1 0 0 1-1-1v-2a1 1 0 1 1 2 0v2a1 1 0 0 1-1 1Zm7-9a1 1 0 1 1 0-2h2a1 1 0 1 1 0 2h-2ZM3 13a1 1 0 1 1 0-2h2a1 1 0 1 1 0 2H3Zm14.9-5.5a1 1 0 0 1-.7-1.7l1.4-1.4A1 1 0 1 1 20 5.8l-1.4 1.4a1 1 0 0 1-.7.3ZM4.7 19.6a1 1 0 0 1 0-1.4l1.4-1.4a1 1 0 0 1 1.4 1.4L6.1 19.6a1 1 0 0 1-1.4 0Zm13.9 0-1.4-1.4a1 1 0 0 1 1.4-1.4l1.4 1.4a1 1 0 0 1-1.4 1.4ZM6.1 7.2 4.7 5.8a1 1 0 1 1 1.4-1.4l1.4 1.4a1 1 0 1 1-1.4 1.4ZM12 17a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z" />
						</svg>
					) : (
						<svg viewBox="0 0 24 24" aria-hidden="true">
							<path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" />
						</svg>
					)}
					<span className="mobile-label">
						{theme === "dark" ? "Light mode" : "Dark mode"}
					</span>
				</button>
				<div
					className="network-toggle"
					role="group"
					aria-label="Application network"
				>
					<button aria-pressed="true">Testnet</button>
					<button aria-pressed="false" disabled>
						Mainnet
					</button>
				</div>
				{account ? (
					<>
						<a
							className="wallet wallet-connect account-pill"
							href="/positions"
							title={account}
							aria-label={`View positions for ${account}`}
							onClick={() => setMenu(false)}
						>
							<span className="connect-short">{short(account, 5, 3)}</span>
							<span className="connect-menu">
								View positions · {short(account)}
							</span>
						</a>
						<button
							className="disconnect-wallet"
							onClick={() => {
								disconnect();
								telemetry("wallet_disconnect", "attempt");
								setMenu(false);
							}}
						>
							Disconnect
						</button>
					</>
				) : (
					<button
						className="wallet wallet-connect"
						onClick={() => void connectTracked(connect)}
					>
						<span className="connect-short">
							{isConnecting ? "Connecting…" : "Connect"}
						</span>
						<span className="connect-menu">
							{isConnecting ? "Connecting…" : "Connect wallet"}
						</span>
					</button>
				)}
			</nav>
			<button
				className="menu"
				onClick={() => setMenu(!menu)}
				aria-expanded={menu}
				aria-label={menu ? "Close menu" : "Open menu"}
			>
				<svg viewBox="0 0 24 24" aria-hidden="true">
					{menu ? (
						<path d="m5.6 4.2 6.4 6.4 6.4-6.4 1.4 1.4-6.4 6.4 6.4 6.4-1.4 1.4-6.4-6.4-6.4 6.4-1.4-1.4 6.4-6.4-6.4-6.4 1.4-1.4Z" />
					) : (
						<path d="M3 6h18v2H3V6Zm0 5h18v2H3v-2Zm0 5h18v2H3v-2Z" />
					)}
				</svg>
			</button>
		</header>
	);
}

function Earn() {
	const { account } = useAccount();
	const { connect } = useConnectUI();
	const { currentConnector } = useCurrentConnector();
	const { wallet } = useWallet();
	const walletAddress = wallet?.address.toString();
	const faucetRun = useRef(0);
	const claimInFlight = useRef<string | null>(null);
	const assetsReady = useRef(false);
	const [amount, setAmount] = useState("5000000");
	const [asset, setAsset] = useState<Asset>("FUEL");
	const [hasGas, setHasGas] = useState(false);
	const [gasRequested, setGasRequested] = useState(false);
	const [waitingForGas, setWaitingForGas] = useState(false);
	const [claimingAssets, setClaimingAssets] = useState(false);
	const [assetsClaimed, setAssetsClaimed] = useState(false);
	const [assetClaimFailed, setAssetClaimFailed] = useState(false);
	const [assetBalances, setAssetBalances] = useState<Record<Asset, bigint>>({
		FUEL: 0n,
		stFUEL: 0n,
	});
	const [balancesLoaded, setBalancesLoaded] = useState(false);
	const [opening, setOpening] = useState(false);
	const [transactionStatus, setTransactionStatus] = useState("");
	const [depositReceipt, setDepositReceipt] = useState<DepositSuccess | null>(
		null,
	);
	const [autoNavigate, setAutoNavigate] = useState(true);
	const [amountFocused, setAmountFocused] = useState(false);
	const [term, setTerm] = useState<TermMonths>(12);
	const [price, setPrice] = useState<OraclePrice | null>(() =>
		lastOraclePrice("FUEL"),
	);
	const [stats, setStats] = useState(ZERO_STATS);
	useEffect(() => {
		let active = true;
		const cached = lastOraclePrice(asset);
		if (cached) setPrice(cached);
		const refreshPrice = () =>
			void oraclePrice(asset)
				.then((quote) => {
					if (active) setPrice(quote);
				})
				.catch(() => {});
		refreshPrice();
		const interval = window.setInterval(refreshPrice, 15_000);
		void getJson<{ stats: Stats }>("/api/v1/stats")
			.then((body) => setStats(body.stats))
			.catch(() => setStats(ZERO_STATS));
		return () => {
			active = false;
			window.clearInterval(interval);
		};
	}, [asset]);
	useEffect(() => {
		if (!depositReceipt || !autoNavigate) return;
		const timer = window.setTimeout(
			() => location.assign("/positions?deposit=success"),
			5_000,
		);
		return () => window.clearTimeout(timer);
	}, [depositReceipt, autoNavigate]);
	useEffect(() => {
		const run = ++faucetRun.current;
		assetsReady.current = false;
		setHasGas(false);
		setGasRequested(false);
		setWaitingForGas(false);
		setAssetsClaimed(false);
		setAssetClaimFailed(false);
		setBalancesLoaded(false);
		setAssetBalances({ FUEL: 0n, stFUEL: 0n });
		setTransactionStatus("");
		if (wallet && account) {
			const testAssets = new Contract(
				TEST_ASSETS_CONTRACT,
				testAssetsAbi,
				wallet,
			);
			void Promise.all([
				wallet.getBalance(),
				refreshAssetBalances(),
				testAssets.functions.has_claimed({ Address: { bits: account } }).get(),
			])
				.then(([gas, balances, claimed]) => {
					if (run !== faucetRun.current || !balances) return;
					const ready =
						Boolean(claimed.value) || hasBothTestAssets(...balances);
					assetsReady.current = ready;
					setAssetsClaimed(ready);
					const selectedRaw = BigInt(
						balances[asset === "FUEL" ? 0 : 1].toString(),
					);
					if (ready && assetRawAmount(amount) > selectedRaw)
						setAmount(earnAmount(selectedRaw));
					if (!gas.gt(0)) return;
					setHasGas(true);
					if (currentConnector?.name === "Burner Wallet" && !ready)
						void claimTestAssets();
				})
				.catch(() => {
					if (run === faucetRun.current) setBalancesLoaded(true);
				});
		}
		return () => {
			faucetRun.current++;
		};
	}, [account, currentConnector?.name, walletAddress]);
	const numericAmount = nonnegativeFinite(Number(amount));
	const selectedBalance = assetBalances[asset];
	const useBalanceControls = Boolean(
		account && balancesLoaded && assetsClaimed,
	);
	const maximumAmount = useBalanceControls
		? earnAmount(selectedBalance)
		: "25000000";
	const maximumNumericAmount = nonnegativeFinite(Number(maximumAmount));
	const presets = useBalanceControls
		? balancePresets(selectedBalance)
		: ([
				["1m", "1000000"],
				["5m", "5000000"],
				["10m", "10000000"],
			] as const);
	const openingValue =
		price === null ? null : nonnegativeFinite(numericAmount * price.price);
	const fixedYield =
		openingValue === null
			? null
			: nonnegativeFinite(yieldUsdc(openingValue, term));
	const sliderProgress = maximumNumericAmount
		? Math.min(100, (numericAmount / maximumNumericAmount) * 100)
		: 0;
	const maturity = Math.floor(Date.now() / 1_000) + TERMS[term].days * DAY;
	const penalty =
		penaltyBps(
			term,
			Math.floor(Date.now() / 1_000),
			maturity,
			Math.floor(Date.now() / 1_000),
		) / 100;
	async function refreshAssetBalances() {
		if (!wallet) return null;
		const run = faucetRun.current;
		const balances = await Promise.all([
			wallet.getBalance(deployments.testnet.fuelAssetId),
			wallet.getBalance(deployments.testnet.stFuelAssetId),
		]);
		if (run === faucetRun.current) {
			const next = {
				FUEL: BigInt(balances[0].toString()),
				stFUEL: BigInt(balances[1].toString()),
			};
			setAssetBalances(next);
			setBalancesLoaded(true);
			if (assetsReady.current && assetRawAmount(amount) > next[asset])
				setAmount(earnAmount(next[asset]));
		}
		return balances;
	}
	async function claimTestAssets() {
		if (
			!wallet ||
			!account ||
			claimInFlight.current === account ||
			assetsReady.current
		)
			return;
		const run = faucetRun.current;
		const claimAccount = account;
		claimInFlight.current = claimAccount;
		setClaimingAssets(true);
		setAssetClaimFailed(false);
		try {
			let balances = await refreshAssetBalances();
			if (!balances) throw new Error("Wallet is unavailable");
			if (!hasBothTestAssets(...balances)) {
				const contract = new Contract(
					TEST_ASSETS_CONTRACT,
					testAssetsAbi,
					wallet,
				);
				await (
					await contract.functions
						.faucet()
						.txParams({ variableOutputs: 2 })
						.call()
				).waitForResult();
				balances = await refreshAssetBalances();
				if (!balances) throw new Error("Wallet is unavailable");
			}
			if (!hasBothTestAssets(...balances))
				throw new Error("Test asset balances were not received");
			if (run === faucetRun.current) {
				assetsReady.current = true;
				setAssetsClaimed(true);
			}
		} catch {
			if (run === faucetRun.current) setAssetClaimFailed(true);
		} finally {
			if (claimInFlight.current === claimAccount) claimInFlight.current = null;
			if (run === faucetRun.current) setClaimingAssets(false);
		}
	}
	async function startFaucetFlow() {
		setGasRequested(true);
		if (!wallet) return;
		const run = ++faucetRun.current;
		setWaitingForGas(true);
		for (
			let attempt = 0;
			attempt < 60 && run === faucetRun.current;
			attempt++
		) {
			try {
				if ((await wallet.getBalance()).gt(0)) {
					setHasGas(true);
					setWaitingForGas(false);
					if (currentConnector?.name === "Burner Wallet")
						await claimTestAssets();
					return;
				}
			} catch {
				/* Retry while the official faucet completes. */
			}
			await new Promise((resolve) => setTimeout(resolve, 2_000));
		}
		if (run === faucetRun.current) {
			setGasRequested(false);
			setWaitingForGas(false);
		}
	}
	const rawAmount = assetRawAmount(amount);
	async function openPosition() {
		if (
			!wallet ||
			!account ||
			opening ||
			rawAmount <= 0n ||
			rawAmount > selectedBalance
		)
			return;
		setOpening(true);
		setTransactionStatus("Reading live O2 and rolling TWAP…");
		try {
			const assetId =
				asset === "FUEL"
					? deployments.testnet.fuelAssetId
					: deployments.testnet.stFuelAssetId;
			const vault = new Contract(
				deployments.testnet.vaultContractId,
				vaultAbi,
				wallet,
			);
			const twapOracle = new Contract(
				deployments.testnet.twapOracleContractId,
				twapOracleAbi,
				wallet,
			);
			const liveOracle = new Contract(
				deployments.testnet.o2PriceFeedContractId,
				o2OracleAbi,
				wallet,
			);
			setTransactionStatus("Confirm deposit in wallet…");
			const transaction = await vault.functions
				.open_position(term)
				.addContracts([twapOracle, liveOracle])
				.callParams({ forward: [rawAmount.toString(), assetId] })
				.txParams({ variableOutputs: 1 })
				.call();
			setTransactionStatus("Opening position…");
			const result = await transaction.waitForResult();
			telemetry("transaction", "success");
			const positionId = String(
				(result as typeof result & { value: string }).value,
			);
			const position = (await vault.functions.get_position(positionId).get())
				.value;
			if (!position) throw new Error("Opened position could not be read");
			await refreshAssetBalances();
			const transactionId =
				"transactionId" in result ? String(result.transactionId) : "";
			const receipt = depositSuccess(
				rawAmount.toString(),
				asset,
				term,
				String(position.opening_price_e6),
				transactionId,
			);
			try {
				sessionStorage.setItem(DEPOSIT_SUCCESS_KEY, JSON.stringify(receipt));
			} catch {
				/* Keep the confirmed receipt in memory. */
			}
			setTransactionStatus("");
			setAutoNavigate(true);
			setDepositReceipt(receipt);
		} catch (reason) {
			telemetry("transaction", "failure");
			setTransactionStatus(
				reason instanceof Error
					? reason.message
					: "Position transaction failed",
			);
		} finally {
			setOpening(false);
		}
	}
	return (
		<main className="earn-page">
			<section className="earn-workbench">
				<div className="earn-left">
					<div className="earn-intro">
						<h1>
							Earn USDC on
							<br />
							your FUEL.
						</h1>
						<p className="lede">
							Lock FUEL or stFUEL and earn up to a{" "}
							<strong>fixed 10% APY</strong>, paid in USDC. Your rate and return
							are locked when you open a position, which becomes a transferable
							bond.
						</p>
						<div className="protocol-badges">
							<span>Built by Fuel Labs</span>
							<span>Audit pending</span>
							<a
								href="https://fuel-reserve.pages.dev/"
								target="_blank"
								rel="noopener noreferrer"
							>
								Fuel Reserve Initiative
							</a>
							<a href="/docs">Learn more →</a>
						</div>
					</div>
					<div className="earn-controls">
						<div className="earn-control-head">
							<label htmlFor="amount">Deposit collateral</label>
							<span>
								{account
									? balancesLoaded
										? `${number(Number(selectedBalance) / 1e9)} ${asset}`
										: "Reading balance…"
									: "Connect to read balance"}
							</span>
						</div>
						<div className="amount">
							<input
								id="amount"
								inputMode="decimal"
								value={amountFocused ? amount : formatAmountInput(amount)}
								onFocus={() => setAmountFocused(true)}
								onBlur={() => setAmountFocused(false)}
								onChange={(event) =>
									setAmount(sanitizeAmountInput(event.target.value, 2))
								}
							/>
							<span className="token-pill asset-picker">
								<img
									src={
										asset === "FUEL"
											? "/resources/fuel-token.png"
											: "/resources/stfuel-token.png"
									}
									alt=""
								/>
								<span className="asset-picker-label">{asset}</span>
								<select
									aria-label="Collateral asset"
									value={asset}
									onChange={(event) => {
										const next = event.target.value as Asset;
										setAsset(next);
										if (useBalanceControls && rawAmount > assetBalances[next])
											setAmount(earnAmount(assetBalances[next]));
									}}
								>
									{" "}
									<option value="FUEL">FUEL</option>
									<option value="stFUEL">stFUEL</option>
								</select>
							</span>
						</div>
						<input
							className="amount-range"
							type="range"
							min="0"
							max={maximumAmount}
							step="0.01"
							value={Math.min(maximumNumericAmount, numericAmount)}
							aria-label="Deposit amount"
							style={{
								background: `linear-gradient(to right, var(--green) ${sliderProgress}%, var(--slider-track) ${sliderProgress}%)`,
							}}
							onChange={(event) => setAmount(event.target.value)}
						/>
						<div className="amount-presets">
							<div>
								{presets.map(([label, value]) => (
									<button
										key={value}
										aria-pressed={amount === value}
										onClick={() => setAmount(value)}
									>
										{label}
									</button>
								))}
							</div>
							<span>
								{openingValue === null
									? "Loading price…"
									: `≈ ${money(openingValue)}`}
							</span>
						</div>
						<label>Term</label>
						<div className="terms">
							{([6, 9, 12] as TermMonths[]).map((months) => (
								<button
									key={months}
									className={term === months ? "selected" : ""}
									onClick={() => setTerm(months)}
								>
									<strong>{months} months</strong>
									<span>{number(months / 1.2, 1)}% fixed return</span>
								</button>
							))}
						</div>
					</div>
				</div>
				<div className="earn-right">
					<div className="earn-result-head">
						<span>You earn</span>
						<small>
							{price === null
								? "Loading on-chain TWAP…"
								: `${price.source} · ${new Date(price.observedAt * 1_000).toLocaleTimeString()}`}
						</small>
					</div>
					<div className="earn-total">
						<img src="/resources/usdc-token.png" alt="" />
						<strong>{fixedYield === null ? "—" : number(fixedYield)}</strong>
						<span>USDC</span>
					</div>
					<p className="earn-availability">
						Paid with your principal at maturity.
					</p>
					<div className="earn-pair">
						<article>
							<span>You lock</span>
							<strong>
								<img
									src={
										asset === "FUEL"
											? "/resources/fuel-token.png"
											: "/resources/stfuel-token.png"
									}
									alt=""
								/>
								{number(numericAmount)} {asset}
							</strong>
							<small>
								{price === null
									? `${asset}/USDC —`
									: `Conservative oracle ${number(price.price, 8)}${asset === "stFUEL" ? " · same per unit" : ""}`}
							</small>
						</article>
						<article>
							<span>You earn at maturity</span>
							<strong className="token-value">
								<img src="/resources/usdc-token.png" alt="" />
								{fixedYield === null ? "— USDC" : `${number(fixedYield)} USDC`}
							</strong>
							<small>Fixed when the position opens</small>
						</article>
					</div>
					<div className="summary earn-breakdown">
						<div>
							<span>Fixed APY</span>
							<strong>10%</strong>
						</div>
						<div>
							<span>Term</span>
							<strong>{term} months</strong>
						</div>
						<div>
							<span>Maturity</span>
							<strong>{date(maturity)}</strong>
						</div>
						<div>
							<span>Opening oracle price</span>
							<strong>{price === null ? "—" : number(price.price, 8)}</strong>
						</div>
						<div>
							<span>Initial early-exit penalty</span>
							<strong>{penalty}%</strong>
						</div>
					</div>
					<div className="onboarding-actions">
						{!account ? (
							<button
								className="primary"
								onClick={() => void connectTracked(connect)}
							>
								Connect wallet to get testnet funds
							</button>
						) : !hasGas ? (
							gasRequested ? (
								<button className="primary" disabled>
									{waitingForGas
										? "Waiting for testnet gas…"
										: "Checking testnet gas…"}
								</button>
							) : (
								<a
									className="primary faucet-action"
									href={faucetUrl(account)}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => void startFaucetFlow()}
								>
									Get testnet ETH
								</a>
							)
						) : !assetsClaimed ? (
							<button
								className="primary"
								disabled={claimingAssets}
								onClick={() => void claimTestAssets()}
							>
								{claimingAssets
									? "Claiming test assets…"
									: assetClaimFailed
										? "Try test assets again"
										: "Get testnet assets"}
							</button>
						) : (
							<>
								<button
									className="primary"
									disabled={
										opening || rawAmount <= 0n || rawAmount > selectedBalance
									}
									onClick={() => void openPosition()}
								>
									{opening
										? transactionStatus
										: `Deposit ${number(numericAmount)} ${asset}`}
								</button>
								<p className="connected-account" role="status">
									{transactionStatus ||
										(rawAmount > selectedBalance
											? `Insufficient ${asset} balance`
											: "Test assets received · ready to deposit")}
								</p>
							</>
						)}
					</div>
				</div>
			</section>
			{depositReceipt && (
				<div className="action-backdrop">
					<section
						className="action-dialog deposit-success-dialog"
						role="dialog"
						aria-modal="true"
						aria-labelledby="deposit-success-title"
					>
						<div className="success-check" aria-hidden="true">
							✓
						</div>
						<h2 id="deposit-success-title">Position opened successfully</h2>
						<p>
							Your collateral is committed and your transferable bond is now in
							your wallet.
						</p>
						<div className="purchase-breakdown">
							<div>
								<span>Collateral committed</span>
								<strong>
									{number(depositReceipt.amount, 9)} {depositReceipt.asset}
								</strong>
							</div>
							<div>
								<span>Term</span>
								<strong>{depositReceipt.term} months</strong>
							</div>
							<div>
								<span>Opening price</span>
								<strong>{depositReceipt.openingPrice} USDC</strong>
							</div>
							<div>
								<span>Fixed return at maturity</span>
								<strong>{number(depositReceipt.reward, 6)} USDC</strong>
							</div>
							<div>
								<span>Expected maturity</span>
								<strong>{date(depositReceipt.maturity)}</strong>
							</div>
						</div>
						{depositReceipt.transactionId && (
							<a
								className="success-explorer"
								href={`https://app-testnet.fuel.network/tx/${depositReceipt.transactionId}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								View transaction ↗
							</a>
						)}
						<a
							className="primary success-position-link"
							href="/positions?deposit=success"
						>
							View my position
						</a>
						<p className="success-countdown">
							Taking you to My Positions in 5 seconds.
						</p>
						<button
							className="stay-button"
							onClick={() => {
								setAutoNavigate(false);
								setDepositReceipt(null);
							}}
						>
							Stay here
						</button>
					</section>
				</div>
			)}
			<section className="stat-grid home-stats" aria-label="Vault statistics">
				<Stat
					label="Total value locked"
					mobileLabel="TVL"
					value={money(stats.tvlUsd)}
				/>
				<Stat
					label="USDC reserves"
					mobileLabel="Reserves"
					value={`${number(stats.reserveUsdc)} USDC`}
				/>
				<Stat
					label="Active positions"
					mobileLabel="Positions"
					value={number(stats.activePositions, 0)}
				/>
				<Stat
					label="FUEL locked"
					mobileLabel="FUEL locked"
					value={`${number(stats.fuelLocked)} FUEL`}
				/>
			</section>
		</main>
	);
}

type PositionAction = "transfer" | "sell" | "cancel" | "unbond" | "claim";

function positionNftAssetId(id: string) {
	const bytes = new Uint8Array(8);
	new DataView(bytes.buffer).setBigUint64(0, BigInt(id));
	return getMintedAssetId(deployments.testnet.vaultContractId, sha256(bytes));
}

function actionError(reason: unknown) {
	const message = reason instanceof Error ? reason.message : String(reason);
	if (/reject|declin|cancel/i.test(message)) return "Wallet request rejected.";
	if (/insufficient|not enough/i.test(message))
		return "Insufficient balance or testnet gas.";
	return message || "Transaction failed.";
}

function readPendingListing() {
	try {
		const pending = JSON.parse(
			sessionStorage.getItem("fuel-latest-listing") || "null",
		) as (Position & { pendingAt?: number }) | null;
		if (pending && !pending.pendingAt) {
			pending.pendingAt = Date.now();
			sessionStorage.setItem("fuel-latest-listing", JSON.stringify(pending));
		}
		if (pending && Date.now() - pending.pendingAt! < 10 * 60_000)
			return pending;
	} catch {
		/* Corrupt session state is discarded below. */
	}
	sessionStorage.removeItem("fuel-latest-listing");
	return null;
}

function Positions() {
	const { account } = useAccount();
	const { wallet } = useWallet();
	const [rows, setRows] = useState<Position[]>([]);
	const [status, setStatus] = useState("all");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [action, setAction] = useState<{
		type: PositionAction;
		position: Position;
	} | null>(null);
	const [recipient, setRecipient] = useState("");
	const [quoteAsset, setQuoteAsset] = useState<"USDC" | "ETH">("USDC");
	const [askingPrice, setAskingPrice] = useState("");
	const [transactionStatus, setTransactionStatus] = useState("");
	const [transactionId, setTransactionId] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [balances, setBalances] = useState<Record<
		"USDC" | "FUEL" | "ETH" | "stFUEL",
		string
	> | null>(null);
	const [depositToast, setDepositToast] = useState<DepositSuccess | null>(() =>
		latestDepositSuccess(),
	);
	useEffect(() => {
		if (!depositToast) return;
		try {
			sessionStorage.removeItem(DEPOSIT_SUCCESS_KEY);
		} catch {
			/* The toast can still dismiss normally. */
		}
		const timer = window.setTimeout(() => setDepositToast(null), 7_000);
		return () => window.clearTimeout(timer);
	}, [depositToast]);
	useEffect(() => {
		if (!account) {
			setRows([]);
			setLoading(false);
			return;
		}
		if (!wallet) {
			setRows([]);
			setLoading(true);
			return;
		}
		setLoading(true);
		let cancelled = false;
		void (async () => {
			const indexed = await getJson<{ positions: Position[] }>(
				`/api/v1/accounts/${account}/positions`,
			)
				.then((body) => body.positions)
				.catch(() => []);
			try {
				const vault = new Contract(
					deployments.testnet.vaultContractId,
					vaultAbi,
					wallet,
				);
				const count = Number(
					(await vault.functions.position_count().get()).value,
				);
				const balances = new Map(
					(await wallet.getBalances()).balances.map((balance) => [
						balance.assetId,
						balance.amount,
					]),
				);
				const owned: number[] = [];
				for (let id = 1; id <= count; id++)
					if (balances.get(positionNftAssetId(String(id)))?.gt(0))
						owned.push(id);
				const direct = await Promise.all(
					owned.map(async (id) => {
						const position = (await vault.functions.get_position(id).get())
							.value;
						const integer = (value: unknown) =>
							String(value).replace(/\.0+$/, "");
						const maturity = unixTime(position.maturity);
						return {
							id: String(id),
							owner: account,
							asset:
								position.collateral_asset.bits ===
								deployments.testnet.fuelAssetId
									? "FUEL"
									: "stFUEL",
							amount: (
								Number(integer(position.collateral_amount)) / 1e9
							).toString(),
							openingPrice: (
								Number(integer(position.opening_price_e6)) / 1e6
							).toString(),
							promisedUsdc: (
								Number(integer(position.promised_usdc)) / 1e6
							).toString(),
							openedAt: unixTime(position.opened_at),
							maturity,
							termMonths: Number(integer(position.term_months)) as TermMonths,
							status: maturity <= Date.now() / 1_000 ? "matured" : "active",
						} satisfies Position;
					}),
				);
				const merged = new Map(
					indexed
						.filter((position) =>
							["listed", "unbonding", "claimed", "early_claimed"].includes(
								position.status,
							),
						)
						.map((position) => [position.id, position]),
				);
				for (const position of direct) merged.set(position.id, position);
				const pending = readPendingListing();
				if (
					pending &&
					indexed.some(
						(position) =>
							position.id === pending.id && position.status === "listed",
					)
				)
					sessionStorage.removeItem("fuel-latest-listing");
				else if (pending?.owner === account) merged.set(pending.id, pending);
				if (!cancelled) {
					setRows([...merged.values()]);
					setError("");
				}
			} catch (reason) {
				const pending = readPendingListing();
				const fallback =
					pending?.owner === account
						? [
								...indexed.filter((position) => position.id !== pending.id),
								pending,
							]
						: indexed;
				if (!cancelled) {
					setRows(fallback);
					setError(
						fallback.length
							? ""
							: reason instanceof Error
								? reason.message
								: "Contract read failed",
					);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [account, wallet]);

	useEffect(() => {
		if (!account || !wallet) {
			setBalances(null);
			return;
		}
		let cancelled = false;
		setBalances(null);
		void wallet
			.getBalances()
			.then(({ balances: walletBalances }) => {
				const byAsset = new Map(
					walletBalances.map((balance) => [
						balance.assetId.toLowerCase(),
						BigInt(balance.amount.toString()),
					]),
				);
				const amount = (assetId: string, decimals: number) =>
					walletBalance(byAsset.get(assetId.toLowerCase()) || 0n, decimals);
				if (!cancelled)
					setBalances({
						USDC: amount(deployments.testnet.usdcAssetId, 6),
						FUEL: amount(deployments.testnet.fuelAssetId, 9),
						ETH: amount(MARKET_V2.ethAssetId, 9),
						stFUEL: amount(deployments.testnet.stFuelAssetId, 9),
					});
			})
			.catch(() => {
				if (!cancelled) setBalances(null);
			});
		return () => {
			cancelled = true;
		};
	}, [account, wallet, transactionId]);

	useEffect(() => {
		if (!action || submitting) return;
		const close = (event: KeyboardEvent) => {
			if (event.key === "Escape") setAction(null);
		};
		addEventListener("keydown", close);
		return () => removeEventListener("keydown", close);
	}, [action, submitting]);

	function openAction(type: PositionAction, position: Position) {
		const projected = projectedBondValue(
			Number(position.amount),
			Number(position.openingPrice),
			Number(position.promisedUsdc),
		);
		setAction({ type, position });
		setRecipient("");
		setQuoteAsset("USDC");
		setAskingPrice(projected.toFixed(6).replace(/\.?0+$/, ""));
		setTransactionStatus("");
		setTransactionId("");
	}

	function changeQuoteAsset(next: "USDC" | "ETH") {
		setQuoteAsset(next);
		if (!action) return;
		const projected = projectedBondValue(
			Number(action.position.amount),
			Number(action.position.openingPrice),
			Number(action.position.promisedUsdc),
		);
		const value =
			next === "USDC"
				? projected
				: projected / nonnegativeFinite(Number(action.position.openingPrice));
		setAskingPrice(
			(Number.isFinite(value) ? value : 0)
				.toFixed(next === "USDC" ? 6 : 9)
				.replace(/\.?0+$/, ""),
		);
	}

	async function confirmAction() {
		if (!action || !wallet || !account || submitting) return;
		setSubmitting(true);
		setTransactionId("");
		setTransactionStatus("Confirm in your wallet…");
		try {
			const id = Number(action.position.id);
			const nft = positionNftAssetId(action.position.id);
			let response;
			if (action.type === "transfer") {
				const destination = new Address(recipient).toB256();
				if (
					destination === new Address(account).toB256() ||
					/^0x0{64}$/.test(destination)
				)
					throw new Error("Enter a different valid Fuel address.");
				const vault = new Contract(
					deployments.testnet.vaultContractId,
					vaultAbi,
					wallet,
				);
				response = await vault.functions
					.transfer_position(id, { Address: { bits: destination } })
					.callParams({ forward: [1, nft] })
					.txParams({ variableOutputs: 1, gasLimit: 1_000_000 })
					.call();
			} else if (action.type === "unbond") {
				const vault = new Contract(
					deployments.testnet.vaultContractId,
					vaultAbi,
					wallet,
				);
				response = await vault.functions
					.start_unbonding(id)
					.callParams({ forward: [1, nft] })
					.txParams({ gasLimit: 1_000_000 })
					.call();
			} else if (action.type === "sell") {
				const rawPrice = decimalRawAmount(
					askingPrice,
					quoteAsset === "USDC" ? 6 : 9,
				);
				if (rawPrice <= 0n || rawPrice > 18_446_744_073_709_551_615n)
					throw new Error("Enter a valid asking price.");
				const market = new Contract(
					deployments.testnet.marketContractId,
					marketAbi,
					wallet,
				);
				const quoteId =
					quoteAsset === "USDC"
						? deployments.testnet.usdcAssetId
						: MARKET_V2.ethAssetId;
				const implementation = new Contract(
					MARKET_V2.marketImplementationContractId,
					marketV2Abi,
					wallet,
				);
				response = await market.functions
					.create_ask(id, { bits: quoteId }, rawPrice, 604_800)
					.addContracts([implementation])
					.callParams({ forward: [1, nft] })
					.txParams({ gasLimit: 1_000_000 })
					.call();
			} else if (action.type === "cancel") {
				const market = new Contract(
					deployments.testnet.marketContractId,
					marketV2Abi,
					wallet,
				);
				const implementation = new Contract(
					MARKET_V2.marketImplementationContractId,
					marketV2Abi,
					wallet,
				);
				const askId = Number(action.position.listing?.id);
				if (!Number.isSafeInteger(askId) || action.position.listing?.legacy)
					throw new Error("This ask cannot be managed in the new market.");
				const method =
					action.position.listing?.expiresAt &&
					action.position.listing.expiresAt <= Date.now() / 1_000
						? market.functions.expire_ask(askId)
						: market.functions.cancel_ask(askId);
				response = await method
					.addContracts([implementation])
					.txParams({ variableOutputs: 1, gasLimit: 1_000_000 })
					.call();
			} else {
				const vault = new Contract(
					deployments.testnet.vaultContractId,
					vaultAbi,
					wallet,
				);
				response = await vault.functions
					.claim_matured(id)
					.callParams({ forward: [1, nft] })
					.txParams({ variableOutputs: 2, gasLimit: 1_000_000 })
					.call();
			}
			setTransactionStatus("Submitting transaction…");
			const result = await response.waitForResult();
			telemetry("transaction", "success");
			setTransactionId(result.transactionId);
			setTransactionStatus(
				action.type === "transfer"
					? "Bond transferred."
					: action.type === "sell"
						? "Bond listed for sale."
						: action.type === "cancel"
							? "Ask cancelled and bond reclaimed."
							: action.type === "unbond"
								? "Unbonding started."
								: "Collateral and USDC reward claimed.",
			);
			const updatedPosition = {
				...action.position,
				status:
					action.type === "sell"
						? "listed"
						: action.type === "cancel"
							? "active"
							: action.type === "unbond"
								? "unbonding"
								: "claimed",
				listing:
					action.type === "sell"
						? { id: "pending", quoteAsset, price: askingPrice, legacy: false }
						: action.type === "cancel"
							? undefined
							: action.position.listing,
			} satisfies Position;
			if (action.type === "sell")
				sessionStorage.setItem(
					"fuel-latest-listing",
					JSON.stringify({ ...updatedPosition, pendingAt: Date.now() }),
				);
			setRows((current) =>
				action.type === "transfer"
					? current.filter((position) => position.id !== action.position.id)
					: [
							...current.filter(
								(position) => position.id !== action.position.id,
							),
							updatedPosition,
						],
			);
		} catch (reason) {
			telemetry("transaction", "failure");
			setTransactionStatus(actionError(reason));
		} finally {
			setSubmitting(false);
		}
	}

	const visible =
		status === "all" ? rows : rows.filter((row) => row.status === status);
	const positionsLoading = Boolean(account && (!wallet || loading));
	const totalClaimable = rows.reduce(
		(sum, position) =>
			sum + rewardBalances(position.promisedUsdc, position.status).claimable,
		0n,
	);
	const totalClaimed = rows.reduce(
		(sum, position) =>
			sum + rewardBalances(position.promisedUsdc, position.status).claimed,
		0n,
	);
	const projected = action
		? projectedBondValue(
				Number(action.position.amount),
				Number(action.position.openingPrice),
				Number(action.position.promisedUsdc),
			)
		: 0;
	const rawPrice =
		action?.type === "sell"
			? decimalRawAmount(askingPrice, quoteAsset === "USDC" ? 6 : 9)
			: 0n;
	const fee = marketFee(rawPrice);
	const currentPenalty = action
		? penaltyBps(
				action.position.termMonths,
				action.position.openedAt,
				action.position.maturity,
				Math.floor(Date.now() / 1_000),
			) / 100
		: 0;
	const actionTitle =
		action?.type === "transfer"
			? "Transfer bond"
			: action?.type === "sell"
				? "Sell this bond"
				: action?.type === "cancel"
					? "Cancel ask"
					: action?.type === "unbond"
						? "Unbond early"
						: "Claim position";
	return (
		<Page
			eyebrow=""
			tag="TESTNET ASSETS · NO VALUE"
			title="My positions"
			description="Manage, transfer, sell and unbond your fixed-term positions."
		>
			{depositToast && (
				<aside className="toast deposit-toast" role="status">
					<span aria-hidden="true">✓</span>
					<div>
						<strong>Deposit confirmed</strong>
						<p>Your bond is now in My Positions.</p>
					</div>
					<button
						aria-label="Dismiss notification"
						onClick={() => setDepositToast(null)}
					>
						×
					</button>
				</aside>
			)}
			<section className="wallet-balance-card" aria-label="Wallet balances">
				<div className="wallet-balance-head">
					<div>
						<h2>Wallet balances</h2>
						<p>Available in your connected wallet</p>
					</div>
					<span>Testnet</span>
				</div>
				<div className="wallet-balance-assets">
					{(["USDC", "FUEL", "ETH", "stFUEL"] as const).map((asset) => (
						<div key={asset}>
							<span>{asset}</span>
							<strong>{balances?.[asset] || "—"}</strong>
						</div>
					))}
				</div>
			</section>
			<div className="tabs">
				{["all", "active", "listed", "unbonding", "matured", "claimed"].map(
					(item) => (
						<button
							className={status === item ? "selected" : ""}
							onClick={() => setStatus(item)}
							key={item}
						>
							{item}
						</button>
					),
				)}
			</div>
			{account && !positionsLoading && (
				<>
					<section className="claim-summary">
						<Metric
							label="Claimable today"
							value={`${decimalAmount(totalClaimable, 6)} USDC`}
						/>
						<Metric
							label="Claimed so far"
							value={`${decimalAmount(totalClaimed, 6)} USDC`}
						/>
					</section>
					<p className="claim-note">
						Fixed USDC rewards unlock in full at maturity. Early unbonding
						forfeits the reward.
					</p>
				</>
			)}
			{!account ? (
				<Empty
					title="Connect your wallet"
					text="Your position NFTs and available actions will appear here."
				/>
			) : positionsLoading ? (
				<Empty
					title="Loading positions…"
					text="Reading your position NFTs and indexed history."
				/>
			) : visible.length === 0 ? (
				<Empty
					title="No positions found"
					text={error || "Open a position to start earning fixed USDC."}
				/>
			) : (
				<div className="cards">
					{visible.map((position) => (
						<PositionCard
							key={position.id}
							position={position}
							onAction={openAction}
						/>
					))}
				</div>
			)}
			{action && (
				<div
					className="action-backdrop"
					onMouseDown={(event) => {
						if (event.target === event.currentTarget && !submitting)
							setAction(null);
					}}
				>
					<section
						className="action-dialog"
						role="dialog"
						aria-modal="true"
						aria-labelledby="action-title"
					>
						<button
							className="dialog-close"
							aria-label="Close dialog"
							disabled={submitting}
							onClick={() => setAction(null)}
						>
							×
						</button>
						<h2 id="action-title">{actionTitle}</h2>
						<div className="dialog-position">
							<strong>
								{number(action.position.amount)} {action.position.asset}
							</strong>
							<span>Matures {date(action.position.maturity)}</span>
						</div>
						{action.type === "transfer" && (
							<>
								<label className="dialog-label" htmlFor="recipient">
									Recipient Fuel address
								</label>
								<input
									id="recipient"
									autoFocus
									value={recipient}
									onChange={(event) => setRecipient(event.target.value.trim())}
									placeholder="fuel… or 0x…"
								/>
								<p className="dialog-warning">
									This is irreversible. The recipient receives the bond NFT,
									collateral claim, maturity rights and fixed return.
								</p>
							</>
						)}
						{action.type === "sell" && (
							<>
								<div className="dialog-value">
									<span>Projected maturity value</span>
									<strong>{money(projected)} USDC</strong>
									<small>
										Opening collateral value plus the bond's fixed USDC return.
										This is a reference, not a guaranteed market price.
									</small>
								</div>
								<div className="dialog-price">
									<label>
										<span>Sell for</span>
										<input
											autoFocus
											inputMode="decimal"
											value={askingPrice}
											onChange={(event) =>
												setAskingPrice(sanitizeAmountInput(event.target.value))
											}
										/>
									</label>
									<label>
										<span>Receive</span>
										<select
											value={quoteAsset}
											onChange={(event) =>
												changeQuoteAsset(event.target.value as "USDC" | "ETH")
											}
										>
											<option>USDC</option>
											<option>ETH</option>
										</select>
									</label>
								</div>
								<p className="dialog-fee">
									Market fee (0.10%):{" "}
									{number(Number(fee) / (quoteAsset === "USDC" ? 1e6 : 1e9), 9)}{" "}
									{quoteAsset} · You receive{" "}
									{number(
										Number(rawPrice - fee) /
											(quoteAsset === "USDC" ? 1e6 : 1e9),
										9,
									)}{" "}
									{quoteAsset}
								</p>
								<p className="dialog-warning">
									Listing escrows this NFT. It cannot be transferred, unbonded
									or claimed until the listing is cancelled or sold.
								</p>
							</>
						)}
						{action.type === "cancel" && (
							<p className="dialog-warning">
								This returns the escrowed bond NFT to its seller. Expired asks
								can be reclaimed by anyone.
							</p>
						)}
						{action.type === "unbond" && (
							<>
								<div className="dialog-value">
									<span>Current early-exit penalty</span>
									<strong>{currentPenalty}%</strong>
									<small>
										{action.position.promisedUsdc} USDC of fixed return is
										forfeited.
									</small>
								</div>
								<p className="dialog-warning">
									Your penalty is locked now. Collateral becomes claimable after
									a 14-day unbonding period. This cannot be reversed.
								</p>
							</>
						)}
						{action.type === "claim" && (
							<>
								<div className="dialog-value">
									<span>Claimable today</span>
									<strong>
										{transactionId
											? "0"
											: number(action.position.promisedUsdc, 6)}{" "}
										USDC
									</strong>
									<small>
										Claimed so far:{" "}
										{transactionId
											? number(action.position.promisedUsdc, 6)
											: "0"}{" "}
										USDC
									</small>
									<small>
										Collateral returned: {number(action.position.amount, 9)}{" "}
										{action.position.asset}
									</small>
								</div>
								<p className="dialog-warning">
									Claiming burns the position NFT and returns the collateral
									plus its fixed USDC reward to your wallet.
								</p>
							</>
						)}
						{transactionStatus && (
							<p
								className={
									transactionId ? "dialog-status success" : "dialog-status"
								}
								role="status"
							>
								{transactionStatus}
								{transactionId && (
									<>
										{" "}
										<a
											href={`https://app-testnet.fuel.network/tx/${transactionId}`}
											target="_blank"
											rel="noopener noreferrer"
										>
											View transaction ↗
										</a>
									</>
								)}
							</p>
						)}
						<div className="dialog-actions">
							<button disabled={submitting} onClick={() => setAction(null)}>
								{transactionId ? "Done" : "Cancel"}
							</button>
							{transactionId && action.type === "sell" && (
								<a
									className="primary dialog-link"
									href={`/market?position=${action.position.id}`}
								>
									View listing
								</a>
							)}
							{!transactionId && (
								<button
									className="primary"
									disabled={
										submitting ||
										(action.type === "transfer"
											? !recipient
											: action.type === "sell"
												? rawPrice <= 0n
												: false)
									}
									onClick={() => void confirmAction()}
								>
									{submitting
										? "Working…"
										: action.type === "transfer"
											? "Confirm transfer"
											: action.type === "sell"
												? "List bond for sale"
												: action.type === "cancel"
													? "Cancel / reclaim ask"
													: action.type === "unbond"
														? "Confirm unbonding"
														: "Claim collateral + reward"}
								</button>
							)}
						</div>
					</section>
				</div>
			)}
		</Page>
	);
}

function PositionCard({
	position,
	onAction,
}: {
	position: Position;
	onAction: (type: PositionAction, position: Position) => void;
}) {
	const now = Math.floor(Date.now() / 1_000);
	const matured = position.status === "matured";
	const meaningfulReward = hasMeaningfulReward(position.promisedUsdc);
	const rewards = rewardBalances(position.promisedUsdc, position.status);
	const claimableToday = rewards.claimable ? position.promisedUsdc : "0";
	const claimedSoFar = rewards.claimed ? position.promisedUsdc : "0";
	const currentPenalty =
		penaltyBps(position.termMonths, position.openedAt, position.maturity, now) /
		100;
	return (
		<article className="position panel">
			<div className="position-head">
				<div>
					<h2>
						{number(position.amount)} {position.asset}
					</h2>
				</div>
				<span className={`pill ${position.status}`}>{position.status}</span>
			</div>
			<dl>
				<div>
					<dt>Claimable today</dt>
					<dd className="reward-value">{number(claimableToday, 6)} USDC</dd>
				</div>
				<div>
					<dt>Claimed so far</dt>
					<dd>{number(claimedSoFar, 6)} USDC</dd>
				</div>
				<div>
					<dt>Fixed reward at maturity</dt>
					<dd>{number(position.promisedUsdc, 6)} USDC</dd>
				</div>
				<div>
					<dt>Maturity</dt>
					<dd>{date(position.maturity)}</dd>
				</div>
				<div>
					<dt>Remaining</dt>
					<dd>{remaining(position.maturity)}</dd>
				</div>
				<div>
					<dt>Exit penalty now</dt>
					<dd>{currentPenalty}%</dd>
				</div>
			</dl>
			{position.status === "active" ? (
				<div className="button-row">
					<button onClick={() => onAction("transfer", position)}>
						Transfer
					</button>
					<button onClick={() => onAction("sell", position)}>Sell Bond</button>
					<button onClick={() => onAction("unbond", position)}>
						Unbond early
					</button>
					<button disabled>Claim</button>
				</div>
			) : position.status === "listed" &&
				position.listing &&
				!position.listing.legacy ? (
				<div className="button-row">
					<button onClick={() => onAction("cancel", position)}>
						{position.listing.expiresAt && position.listing.expiresAt <= now
							? "Reclaim expired ask"
							: "Cancel ask"}
					</button>
				</div>
			) : matured ? (
				<div className="button-row claim-actions">
					{meaningfulReward ? (
						<button
							className="primary"
							onClick={() => onAction("claim", position)}
						>
							Claim {number(position.promisedUsdc, 6)} USDC +{" "}
							{number(position.amount, 9)} {position.asset}
						</button>
					) : (
						<>
							<button disabled>Reward below 0.1 USDC</button>
							<button
								className="primary"
								onClick={() => onAction("claim", position)}
							>
								Withdraw {number(position.amount, 9)} {position.asset}
							</button>
						</>
					)}
				</div>
			) : (
				<p className="position-state">
					This bond has no available wallet actions in its current state.
				</p>
			)}
		</article>
	);
}

function Market() {
	const { account } = useAccount();
	const { wallet } = useWallet();
	const { connect } = useConnectUI();
	const [orders, setOrders] = useState<Position[]>([]);
	const [query, setQuery] = useState(
		new URLSearchParams(location.search).get("position") || "",
	);
	const [quote, setQuote] = useState("all");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [retry, setRetry] = useState(0);
	const [nextCursor, setNextCursor] = useState<number | null>(null);
	const [loadingMore, setLoadingMore] = useState(false);
	const loadedPages = useRef(1);
	const [purchase, setPurchase] = useState<Position | null>(null);
	const [purchaseStatus, setPurchaseStatus] = useState("");
	const [purchaseId, setPurchaseId] = useState("");
	const [purchasing, setPurchasing] = useState(false);
	const [bids, setBids] = useState<Bid[]>([]);
	const [bidBudget, setBidBudget] = useState("");
	const [bidPrice, setBidPrice] = useState("");
	const [bidCollateral, setBidCollateral] = useState("0");
	const [bidTerm, setBidTerm] = useState("0");
	const [bidMinDays, setBidMinDays] = useState("");
	const [bidMaxDays, setBidMaxDays] = useState("");
	const [bidMinCollateral, setBidMinCollateral] = useState("");
	const [bidMaxCollateral, setBidMaxCollateral] = useState("");
	const [bidMinValue, setBidMinValue] = useState("");
	const [bidMaxValue, setBidMaxValue] = useState("");
	const [fillPosition, setFillPosition] = useState("");
	const [bidStatus, setBidStatus] = useState("");
	const [bidBusy, setBidBusy] = useState(false);
	useEffect(() => {
		let cancelled = false;
		const pending = readPendingListing();
		if (pending) setOrders([pending]);
		const refresh = async () => {
			try {
				const canonical: Position[] = [];
				let cursor = 0;
				let following: number | null = null;
				for (
					let pageNumber = 0;
					pageNumber < loadedPages.current;
					pageNumber++
				) {
					const body = await getJson<{
						orders: Position[];
						nextCursor: number | null;
					}>(`/api/v1/market/orders?status=active&limit=100&cursor=${cursor}`);
					canonical.push(...body.orders);
					following = body.nextCursor;
					if (following === null) break;
					cursor = following;
				}
				const bidBody = await getJson<{ bids: Bid[] }>(
					"/api/v1/market/bids?status=active&includeExpired=1&limit=100",
				);
				if (cancelled) return;
				setBids(bidBody.bids);
				const currentPending = readPendingListing();
				const indexedPending =
					currentPending &&
					canonical.some((position) => position.id === currentPending.id);
				if (indexedPending) sessionStorage.removeItem("fuel-latest-listing");
				const merged = new Map<string, Position>(
					currentPending && !indexedPending
						? [[currentPending.id, currentPending]]
						: [],
				);
				for (const position of canonical) merged.set(position.id, position);
				setOrders([...merged.values()]);
				setNextCursor(following);
				setError("");
			} catch {
				if (!cancelled) setError("Active listings could not be refreshed.");
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		void refresh();
		const timer = setInterval(() => void refresh(), 15_000);
		return () => {
			cancelled = true;
			clearInterval(timer);
		};
	}, [retry]);
	useEffect(() => {
		if (!purchase || purchasing) return;
		const close = (event: KeyboardEvent) => {
			if (event.key === "Escape") setPurchase(null);
		};
		addEventListener("keydown", close);
		return () => removeEventListener("keydown", close);
	}, [purchase, purchasing]);

	async function loadMore() {
		if (nextCursor === null || loadingMore) return;
		setLoadingMore(true);
		try {
			const body = await getJson<{
				orders: Position[];
				nextCursor: number | null;
			}>(`/api/v1/market/orders?status=active&limit=100&cursor=${nextCursor}`);
			setOrders((current) => {
				const merged = new Map(
					current.map((position) => [position.id, position]),
				);
				for (const position of body.orders) merged.set(position.id, position);
				return [...merged.values()];
			});
			loadedPages.current++;
			setNextCursor(body.nextCursor);
			setError("");
		} catch {
			setError("More active asks could not be loaded.");
		} finally {
			setLoadingMore(false);
		}
	}

	async function buyBond() {
		if (!purchase || !wallet || !account || purchasing) return;
		setPurchasing(true);
		setPurchaseId("");
		try {
			const listing = purchase.listing;
			const orderId = Number(listing?.id);
			if (!listing || !Number.isSafeInteger(orderId) || orderId <= 0)
				throw new Error(
					"This listing is still being confirmed. Try again shortly.",
				);
			if (purchase.owner.toLowerCase() === account.toLowerCase())
				throw new Error("You cannot buy your own listing.");
			const quoteId =
				listing.quoteAsset === "USDC"
					? deployments.testnet.usdcAssetId
					: listing.quoteAsset === "ETH"
						? MARKET_V2.ethAssetId
						: deployments.testnet.fuelAssetId;
			const rawPrice = decimalRawAmount(
				listing.price,
				listing.quoteAsset === "USDC" ? 6 : 9,
			);
			if (rawPrice <= 0n || rawPrice > 18_446_744_073_709_551_615n)
				throw new Error("This listing has an invalid price.");
			setPurchaseStatus(`Checking ${listing.quoteAsset} balance…`);
			if (BigInt((await wallet.getBalance(quoteId)).toString()) < rawPrice)
				throw new Error(`Insufficient ${listing.quoteAsset} balance.`);
			setPurchaseStatus("Confirm purchase in your wallet…");
			const market = new Contract(
				listing.legacy
					? MARKET_V2.legacyMarketContractId
					: deployments.testnet.marketContractId,
				listing.legacy ? marketAbi : marketV2Abi,
				wallet,
			);
			const response = listing.legacy
				? await market.functions
						.buy(orderId)
						.callParams({ forward: [rawPrice.toString(), quoteId] })
						.txParams({ variableOutputs: 2, gasLimit: 1_000_000 })
						.call()
				: await market.functions
						.buy_ask(orderId)
						.addContracts([
							new Contract(
								MARKET_V2.marketImplementationContractId,
								marketV2Abi,
								wallet,
							),
						])
						.callParams({ forward: [rawPrice.toString(), quoteId] })
						.txParams({ variableOutputs: 2, gasLimit: 1_000_000 })
						.call();
			setPurchaseStatus("Submitting purchase…");
			const result = await response.waitForResult();
			telemetry("transaction", "success");
			setPurchaseId(result.transactionId);
			setPurchaseStatus(
				"Purchase complete. The bond NFT is now in your wallet.",
			);
			setOrders((current) =>
				current.filter((position) => position.id !== purchase.id),
			);
			const pending = readPendingListing();
			if (pending?.id === purchase.id)
				sessionStorage.removeItem("fuel-latest-listing");
		} catch (reason) {
			telemetry("transaction", "failure");
			const message = actionError(reason);
			setPurchaseStatus(
				/ORDER_NOT_ACTIVE|NOT_ACTIVE/i.test(message)
					? "This listing was already filled or cancelled."
					: message,
			);
		} finally {
			setPurchasing(false);
		}
	}

	async function runBid(
		action: "create" | "cancel" | "expire" | "fill",
		bid?: Bid,
	) {
		if (!wallet || !account || bidBusy) return;
		setBidBusy(true);
		setBidStatus("Confirm in your wallet…");
		try {
			const market = new Contract(
				deployments.testnet.marketContractId,
				marketV2Abi,
				wallet,
			);
			const implementation = new Contract(
				MARKET_V2.marketImplementationContractId,
				marketV2Abi,
				wallet,
			);
			let response;
			if (action === "create") {
				const maximum = (1n << 64n) - 1n;
				const budget = decimalRawAmount(bidBudget, 6);
				const price = decimalRawAmount(bidPrice, 6);
				const minCollateral = decimalRawAmount(bidMinCollateral, 9),
					maxCollateral = decimalRawAmount(bidMaxCollateral, 9);
				const minValue = decimalRawAmount(bidMinValue, 6),
					maxValue = decimalRawAmount(bidMaxValue, 6);
				const minSeconds = BigInt(bidMinDays || 0) * BigInt(DAY),
					maxSeconds = BigInt(bidMaxDays || 0) * BigInt(DAY);
				if (budget <= 0n || price <= 0n || budget % price !== 0n)
					throw new Error(
						"Budget must be a whole multiple of the per-bond price.",
					);
				if (
					[
						budget,
						price,
						minCollateral,
						maxCollateral,
						minValue,
						maxValue,
						minSeconds,
						maxSeconds,
					].some((value) => value > maximum)
				)
					throw new Error("One of the order values is too large.");
				if (
					(maxSeconds && maxSeconds < minSeconds) ||
					(maxCollateral && maxCollateral < minCollateral) ||
					(maxValue && maxValue < minValue)
				)
					throw new Error(
						"Maximum filters must be greater than or equal to minimum filters.",
					);
				if (
					BigInt(
						(
							await wallet.getBalance(deployments.testnet.usdcAssetId)
						).toString(),
					) < budget
				)
					throw new Error("Insufficient USDC balance.");
				response = await market.functions
					.create_buy_order(
						price.toString(),
						Number(bidCollateral),
						Number(bidTerm),
						minSeconds.toString(),
						maxSeconds.toString(),
						minCollateral.toString(),
						maxCollateral.toString(),
						minValue.toString(),
						maxValue.toString(),
						604_800,
					)
					.addContracts([implementation])
					.callParams({
						forward: [budget.toString(), deployments.testnet.usdcAssetId],
					})
					.call();
			} else {
				if (!bid) return;
				const id = Number(bid.id);
				if (action === "fill") {
					const positionId = Number(fillPosition);
					if (!Number.isSafeInteger(positionId) || positionId <= 0)
						throw new Error("Enter the position ID you want to sell.");
					response = await market.functions
						.fill_buy_order(id, positionId)
						.addContracts([
							implementation,
							new Contract(
								deployments.testnet.vaultContractId,
								vaultAbi,
								wallet,
							),
						])
						.callParams({
							forward: [1, positionNftAssetId(String(positionId))],
						})
						.txParams({ variableOutputs: 2, gasLimit: 1_000_000 })
						.call();
				} else {
					response = await (action === "expire"
						? market.functions.expire_buy_order(id)
						: market.functions.cancel_buy_order(id)
					)
						.addContracts([implementation])
						.txParams({ variableOutputs: 1, gasLimit: 1_000_000 })
						.call();
				}
			}
			const result = await response.waitForResult();
			telemetry("transaction", "success");
			setBidStatus(`Transaction confirmed: ${result.transactionId}`);
			setBidBudget("");
			setBidPrice("");
			setRetry((value) => value + 1);
		} catch (reason) {
			telemetry("transaction", "failure");
			setBidStatus(actionError(reason));
		} finally {
			setBidBusy(false);
		}
	}

	const visible = useMemo(
		() =>
			orders.filter(
				(order) =>
					(!query ||
						order.id.includes(query) ||
						order.asset.toLowerCase().includes(query.toLowerCase())) &&
					(quote === "all" || order.listing?.quoteAsset === quote),
			),
		[orders, query, quote],
	);
	return (
		<Page
			eyebrow=""
			title="Bond Market"
			description="Buy listed bonds directly or escrow a general USDC order that any qualifying bond can fill."
		>
			<div className="market-tools">
				<input
					aria-label="Search positions"
					placeholder="Search position or asset"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
				/>
				<select
					aria-label="Quote asset"
					value={quote}
					onChange={(event) => setQuote(event.target.value)}
				>
					<option value="all">All quote assets</option>
					<option>ETH</option>
					<option>FUEL</option>
					<option>USDC</option>
				</select>
			</div>
			<div className="market-count">
				<strong>Active asks loaded ({orders.length})</strong>
				<div>
					{error && <span role="alert">{error}</span>}
					<button
						onClick={() => {
							loadedPages.current = 1;
							setLoading(true);
							setRetry((value) => value + 1);
						}}
					>
						Refresh
					</button>
				</div>
			</div>
			<div className="table-wrap">
				<table>
					<thead>
						<tr>
							<th>Position</th>
							<th>Collateral</th>
							<th>Maturity</th>
							<th>Fixed USDC</th>
							<th>Asking price</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{visible.map((position) => (
							<tr key={position.id}>
								<td>#{position.id}</td>
								<td>
									{number(position.amount)} {position.asset}
								</td>
								<td>
									{date(position.maturity)}
									<small>{remaining(position.maturity)}</small>
								</td>
								<td>{position.promisedUsdc} USDC</td>
								<td>
									{position.listing?.price} {position.listing?.quoteAsset}
								</td>
								<td>
									{account?.toLowerCase() === position.owner.toLowerCase() ? (
										<button className="small" disabled>
											Your listing
										</button>
									) : (
										<button
											className="primary small"
											onClick={() => {
												setPurchase(position);
												setPurchaseStatus("");
												setPurchaseId("");
											}}
										>
											Buy
										</button>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
				{loading && !orders.length ? (
					<Empty
						title="Loading active listings…"
						text="Reading up to 100 BondMarket asks."
					/>
				) : (
					!visible.length && (
						<Empty
							title={
								orders.length ? "No matching listings" : "No active listings"
							}
							text={
								orders.length
									? "Clear the search or quote-asset filter."
									: "No bonds are currently offered for sale."
							}
						/>
					)
				)}
			</div>
			{nextCursor !== null && (
				<div className="market-pagination">
					<button disabled={loadingMore} onClick={() => void loadMore()}>
						{loadingMore ? "Loading…" : "Load 100 more"}
					</button>
				</div>
			)}
			{purchase && (
				<div
					className="action-backdrop"
					onMouseDown={(event) => {
						if (event.target === event.currentTarget && !purchasing)
							setPurchase(null);
					}}
				>
					<section
						className="action-dialog"
						role="dialog"
						aria-modal="true"
						aria-labelledby="buy-title"
					>
						<button
							className="dialog-close"
							aria-label="Close dialog"
							disabled={purchasing}
							onClick={() => setPurchase(null)}
						>
							×
						</button>
						<h2 id="buy-title">Buy this bond</h2>
						<div className="dialog-position">
							<strong>
								{number(purchase.amount, 9)} {purchase.asset}
							</strong>
							<span>
								Matures {date(purchase.maturity)} ·{" "}
								{remaining(purchase.maturity)}
							</span>
						</div>
						<div className="purchase-breakdown">
							<div>
								<span>Fixed USDC reward</span>
								<strong>{number(purchase.promisedUsdc, 6)} USDC</strong>
							</div>
							<div>
								<span>Asking price</span>
								<strong>
									{purchase.listing?.price} {purchase.listing?.quoteAsset}
								</strong>
							</div>
							<div>
								<span>Market fee (seller pays)</span>
								<strong>
									{purchase.listing
										? number(
												Number(
													marketFee(
														decimalRawAmount(
															purchase.listing.price,
															purchase.listing.quoteAsset === "USDC" ? 6 : 9,
														),
													),
												) /
													(purchase.listing.quoteAsset === "USDC" ? 1e6 : 1e9),
												9,
											)
										: 0}{" "}
									{purchase.listing?.quoteAsset}
								</strong>
							</div>
							<div className="purchase-total">
								<span>You pay</span>
								<strong>
									{purchase.listing?.price} {purchase.listing?.quoteAsset}
								</strong>
							</div>
						</div>
						<p className="dialog-warning">
							You receive the existing position NFT, collateral claim and
							fixed-return rights. Vault collateral remains under
							managed-custody risk.
						</p>
						{purchaseStatus && (
							<p
								className={
									purchaseId ? "dialog-status success" : "dialog-status"
								}
								role="status"
							>
								{purchaseStatus}
								{purchaseId && (
									<>
										{" "}
										<a
											href={`https://app-testnet.fuel.network/tx/${purchaseId}`}
											target="_blank"
											rel="noopener noreferrer"
										>
											View transaction ↗
										</a>
									</>
								)}
							</p>
						)}
						<div className="dialog-actions">
							<button disabled={purchasing} onClick={() => setPurchase(null)}>
								{purchaseId ? "Done" : "Cancel"}
							</button>
							{purchaseId ? (
								<a className="primary dialog-link" href="/positions">
									View position
								</a>
							) : account ? (
								<button
									className="primary"
									disabled={purchasing}
									onClick={() => void buyBond()}
								>
									{purchasing
										? "Working…"
										: `Confirm purchase · ${purchase.listing?.price} ${purchase.listing?.quoteAsset}`}
								</button>
							) : (
								<button
									className="primary"
									onClick={() => void connectTracked(connect)}
								>
									Connect wallet to continue
								</button>
							)}
						</div>
					</section>
				</div>
			)}
			<section className="page-head">
				<p className="eyebrow">BUY SIDE</p>
				<h2>Open bond buy orders</h2>
				<p>
					Escrow a USDC budget and define the kind of bonds you will buy.
					Multiple sellers can fill one order with qualifying whole position
					NFTs.
				</p>
			</section>
			<section className="panel buy-order-form">
				<div className="form-grid">
					<label>
						Total budget (USDC)
						<input
							inputMode="decimal"
							placeholder="10,000"
							value={bidBudget}
							onChange={(event) =>
								setBidBudget(sanitizeAmountInput(event.target.value))
							}
						/>
					</label>
					<label>
						Price per bond (USDC)
						<input
							inputMode="decimal"
							placeholder="500"
							value={bidPrice}
							onChange={(event) =>
								setBidPrice(sanitizeAmountInput(event.target.value))
							}
						/>
					</label>
					<label>
						Collateral
						<select
							value={bidCollateral}
							onChange={(event) => setBidCollateral(event.target.value)}
						>
							<option value="0">Any collateral</option>
							<option value="1">FUEL</option>
							<option value="2">stFUEL</option>
						</select>
					</label>
					<label>
						Original term
						<select
							value={bidTerm}
							onChange={(event) => setBidTerm(event.target.value)}
						>
							<option value="0">Any term</option>
							<option value="6">6 months</option>
							<option value="9">9 months</option>
							<option value="12">12 months</option>
						</select>
					</label>
					<label>
						Minimum days remaining
						<input
							inputMode="numeric"
							placeholder="Any"
							value={bidMinDays}
							onChange={(event) =>
								setBidMinDays(event.target.value.replace(/\D/g, ""))
							}
						/>
					</label>
					<label>
						Maximum days remaining
						<input
							inputMode="numeric"
							placeholder="Any"
							value={bidMaxDays}
							onChange={(event) =>
								setBidMaxDays(event.target.value.replace(/\D/g, ""))
							}
						/>
					</label>
					<label>
						Minimum collateral
						<input
							inputMode="decimal"
							placeholder="Any"
							value={bidMinCollateral}
							onChange={(event) =>
								setBidMinCollateral(sanitizeAmountInput(event.target.value))
							}
						/>
					</label>
					<label>
						Maximum collateral
						<input
							inputMode="decimal"
							placeholder="Any"
							value={bidMaxCollateral}
							onChange={(event) =>
								setBidMaxCollateral(sanitizeAmountInput(event.target.value))
							}
						/>
					</label>
					<label>
						Minimum maturity value (USDC)
						<input
							inputMode="decimal"
							placeholder="Any"
							value={bidMinValue}
							onChange={(event) =>
								setBidMinValue(sanitizeAmountInput(event.target.value))
							}
						/>
					</label>
					<label>
						Maximum maturity value (USDC)
						<input
							inputMode="decimal"
							placeholder="Any"
							value={bidMaxValue}
							onChange={(event) =>
								setBidMaxValue(sanitizeAmountInput(event.target.value))
							}
						/>
					</label>
				</div>
				{account ? (
					<button
						className="primary"
						disabled={bidBusy}
						onClick={() => void runBid("create")}
					>
						Create buy order
					</button>
				) : (
					<button
						className="primary"
						onClick={() => void connectTracked(connect)}
					>
						Connect to create an order
					</button>
				)}
			</section>
			{bidStatus && (
				<p className="status-message" role="status">
					{bidStatus}
				</p>
			)}
			<div className="market-tools">
				<input
					aria-label="Position ID to sell"
					inputMode="numeric"
					placeholder="Your position ID to fill an order"
					value={fillPosition}
					onChange={(event) =>
						setFillPosition(event.target.value.replace(/\D/g, ""))
					}
				/>
			</div>
			<div className="table-wrap">
				<table>
					<thead>
						<tr>
							<th>Order</th>
							<th>Requirements</th>
							<th>Price / NFT</th>
							<th>Budget left</th>
							<th>Filled</th>
							<th>Expires</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{bids.map((bid) => {
							const expired = bid.expiresAt <= Date.now() / 1_000;
							const bidder =
								account?.toLowerCase() === bid.bidder.toLowerCase();
							const requirements = [
								bid.collateral || "Any collateral",
								bid.termMonths ? `${bid.termMonths} months` : "Any term",
								bid.minRemainingSeconds || bid.maxRemainingSeconds
									? `${number(bid.minRemainingSeconds / DAY, 0)}–${bid.maxRemainingSeconds ? number(bid.maxRemainingSeconds / DAY, 0) : "∞"} days`
									: "Any maturity",
								bid.minCollateral || bid.maxCollateral
									? `${bid.minCollateral || "0"}–${bid.maxCollateral || "∞"} collateral`
									: "Any size",
								bid.minPromisedUsdc || bid.maxPromisedUsdc
									? `${bid.minPromisedUsdc || "0"}–${bid.maxPromisedUsdc || "∞"} USDC value`
									: "Any value",
							].join(" · ");
							return (
								<tr key={bid.id}>
									<td>#{bid.id}</td>
									<td>{requirements}</td>
									<td>{bid.pricePerBond} USDC</td>
									<td>
										{bid.remaining} / {bid.budget} USDC
									</td>
									<td>{bid.filledCount}</td>
									<td>{expired ? "Expired" : date(bid.expiresAt)}</td>
									<td>
										{bidder ? (
											<button
												disabled={bidBusy}
												onClick={() =>
													void runBid(expired ? "expire" : "cancel", bid)
												}
											>
												{expired ? "Reclaim" : "Cancel"}
											</button>
										) : account && !expired ? (
											<button
												className="primary small"
												disabled={bidBusy || !fillPosition}
												onClick={() => void runBid("fill", bid)}
											>
												Sell bond
											</button>
										) : null}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
				{!bids.length && (
					<Empty
						title="No open buy orders"
						text="Create the first general USDC buy order."
					/>
				)}
			</div>
			<p className="fine wide">
				Each fill transfers one whole position NFT for the fixed per-bond price.
				Optional filters are enforced against the Vault on-chain. A 10 bp fee is
				deducted from seller proceeds.
			</p>
		</Page>
	);
}

function StatsPage() {
	const [stats, setStats] = useState(ZERO_STATS);
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState("");
	useEffect(() => {
		let active = true;
		const refresh = () =>
			void getJson<{ stats: Stats }>("/api/v1/stats")
				.then((body) => {
					if (active) {
						setStats(body.stats);
						setLoaded(true);
						setError("");
					}
				})
				.catch(() => {
					if (active) setError("Market statistics could not be refreshed.");
				});
		refresh();
		const timer = window.setInterval(refresh, 30_000);
		return () => {
			active = false;
			window.clearInterval(timer);
		};
	}, []);
	return (
		<Page
			eyebrow="PROTOCOL & MARKET"
			title="Bond market stats"
			description="Issuance, trading flow, participation and reserve health for Fuel Earn bonds."
		>
			{error && (
				<p className="status-message" role="alert">
					{error}
				</p>
			)}
			{!loaded ? (
				<Empty
					title="Loading market statistics…"
					text="Reading issuance and trading activity."
				/>
			) : (
				<>
					<div className="stat-grid">
						<Stat label="Total value locked" value={money(stats.tvlUsd)} />
						<Stat label="Bonds issued" value={number(stats.bondsIssued, 0)} />
						<Stat
							label="Outstanding bonds"
							value={number(stats.bondsOutstanding, 0)}
						/>
						<Stat label="Active asks" value={number(stats.activeOrders, 0)} />
					</div>
					<div className="split stats-panels">
						<section className="panel">
							<p className="eyebrow">BOND ISSUANCE</p>
							<Metric
								label="Opening-value volume"
								value={`${number(stats.issuanceVolumeUsdc)} USDC`}
							/>
							<Metric
								label="Fixed returns issued"
								value={`${number(stats.fixedReturnsIssuedUsdc)} USDC`}
							/>
							<Metric
								label="6-month bonds"
								value={number(stats.bonds6Months, 0)}
							/>
							<Metric
								label="9-month bonds"
								value={number(stats.bonds9Months, 0)}
							/>
							<Metric
								label="12-month bonds"
								value={number(stats.bonds12Months, 0)}
							/>
							<Metric
								label="Matured / settled / early exited"
								value={`${number(stats.bondsMatured, 0)} / ${number(stats.bondsSettled, 0)} / ${number(stats.bondsEarlyExited, 0)}`}
							/>
						</section>
						<section className="panel">
							<p className="eyebrow">MARKET HEALTH</p>
							<Metric label="Asks posted" value={number(stats.asksPosted, 0)} />
							<Metric
								label="Sales completed"
								value={number(stats.ordersFilled, 0)}
							/>
							<Metric
								label="Orders cancelled"
								value={number(stats.ordersCancelled, 0)}
							/>
							<Metric
								label="Completed-order fill rate"
								value={`${number(stats.fillRateBps / 100)}%`}
							/>
							<Metric
								label="Unique sellers / buyers"
								value={`${number(stats.uniqueSellers, 0)} / ${number(stats.uniqueBuyers, 0)}`}
							/>
							<Metric
								label="Average time to fill"
								value={duration(stats.averageFillSeconds)}
							/>
						</section>
						<section className="panel">
							<p className="eyebrow">24-HOUR MARKET FLOW</p>
							<Metric label="New asks" value={number(stats.asks24h, 0)} />
							<Metric label="Sales" value={number(stats.fills24h, 0)} />
							<Metric
								label="Cancellations"
								value={number(stats.cancellations24h, 0)}
							/>
							<Metric
								label="USDC volume"
								value={`${number(stats.volumeUsdc24h)} USDC`}
							/>
							<Metric
								label="FUEL volume"
								value={`${number(stats.volumeFuel24h)} FUEL`}
							/>
							<Metric
								label="Lifetime volume"
								value={`${number(stats.salesVolumeUsdc)} USDC · ${number(stats.salesVolumeFuel)} FUEL`}
							/>
						</section>
						<section className="panel">
							<p className="eyebrow">VAULT & FEES</p>
							<Metric
								label="Fixed USDC obligations"
								value={`${number(stats.promisedUsdc)} USDC`}
							/>
							<Metric
								label="USDC reserves"
								value={`${number(stats.reserveUsdc)} USDC`}
							/>
							<Metric
								label="Reserve coverage"
								value={`${number(stats.coverageBps / 100)}%`}
							/>
							<Metric
								label="FUEL / stFUEL locked"
								value={`${number(stats.fuelLocked)} / ${number(stats.stFuelLocked)}`}
							/>
							<Metric
								label="Fees accrued"
								value={`${number(stats.accruedFeesUsdc)} USDC · ${number(stats.accruedFeesFuel)} FUEL`}
							/>
							<Metric
								label="Indexed block"
								value={
									stats.blockHeight
										? number(stats.blockHeight, 0)
										: "Awaiting index"
								}
							/>
						</section>
					</div>
					<p className="fine wide">
						FUEL and USDC trading volumes remain separate because historical
						FUEL trades do not have an indexed execution-time USD price.
						Contract state is authoritative; index status:{" "}
						{stats.reconciliationStatus || "pending"}.
					</p>
				</>
			)}
		</Page>
	);
}

function Faucet() {
	const { account } = useAccount();
	const { connect } = useConnectUI();
	return (
		<Page
			eyebrow="TESTNET FUNDS"
			title="Official Fuel faucet"
			description="Fund a connected native Fuel wallet with testnet gas before opening a position."
		>
			<div className="faucet-grid">
				<article className="panel">
					<h2>Fuel testnet</h2>
					<p>
						The official Fuel faucet pre-fills your connected wallet address.
					</p>
					{account ? (
						<a
							className="primary faucet-action"
							href={faucetUrl(account)}
							target="_blank"
							rel="noopener noreferrer"
						>
							Open official faucet
						</a>
					) : (
						<button
							className="primary"
							onClick={() => void connectTracked(connect)}
						>
							Connect wallet
						</button>
					)}
				</article>
			</div>
		</Page>
	);
}

function Docs() {
	return (
		<Page
			eyebrow="DOCUMENTATION"
			title="Fuel Earn docs"
			description="Guides, protocol mechanics, risks, deployments, APIs and operational controls."
		>
			<div className="docs-redirect panel">
				<p>
					The full documentation now lives at <strong>docs.fuel.place</strong>.
				</p>
				<a className="primary link-button" href="https://docs.fuel.place">
					Open documentation →
				</a>
			</div>
		</Page>
	);
}

function Admin() {
	const { account } = useAccount();
	const { connect } = useConnectUI();
	const { wallet } = useWallet();
	const [manager, setManager] = useState("");
	const [proxyOwner, setProxyOwner] = useState("");
	const [strategy, setStrategy] = useState("");
	const [nextManager, setNextManager] = useState("");
	const [nextMarketOwner, setNextMarketOwner] = useState("");
	const [nextProxyOwner, setNextProxyOwner] = useState("");
	const [asset, setAsset] = useState<Asset>("FUEL");
	const [amount, setAmount] = useState("");
	const [reserveAmount, setReserveAmount] = useState("");
	const [withdrawAsset, setWithdrawAsset] = useState<Asset | "USDC">("USDC");
	const [withdrawAmount, setWithdrawAmount] = useState("");
	const [feeAsset, setFeeAsset] = useState<"ETH" | "USDC">("USDC");
	const [feeBps, setFeeBps] = useState("10");
	const [balances, setBalances] = useState({
		reserve: "—",
		reserveObligation: "—",
		reserveFree: "—",
		fuelPrincipal: "—",
		fuelDeployed: "—",
		fuelPenalties: "—",
		fuelFree: "—",
		stFuelPrincipal: "—",
		stFuelDeployed: "—",
		stFuelPenalties: "—",
		stFuelFree: "—",
		fee: "—",
	});
	const [status, setStatus] = useState("");
	const [transactionId, setTransactionId] = useState("");
	const [busy, setBusy] = useState(false);
	const validAddress = (value: string) => /^0x[0-9a-fA-F]{64}$/.test(value);
	const identity = (value: unknown) =>
		(value as { Address?: { bits?: string } }).Address?.bits || "";
	const isManager = Boolean(
		account && manager && account.toLowerCase() === manager.toLowerCase(),
	);
	const isProxyOwner = Boolean(
		account && proxyOwner && account.toLowerCase() === proxyOwner.toLowerCase(),
	);
	const assetId =
		asset === "FUEL"
			? deployments.testnet.fuelAssetId
			: deployments.testnet.stFuelAssetId;
	const feeAssetId =
		feeAsset === "ETH" ? MARKET_V2.ethAssetId : deployments.testnet.usdcAssetId;
	const withdrawAssetId =
		withdrawAsset === "USDC"
			? deployments.testnet.usdcAssetId
			: withdrawAsset === "FUEL"
				? deployments.testnet.fuelAssetId
				: deployments.testnet.stFuelAssetId;

	async function refresh() {
		try {
			const provider =
				wallet?.provider || new Provider(deployments.testnet.providerUrl);
			const vault = new Contract(
				deployments.testnet.vaultContractId,
				vaultAbi,
				provider,
			);
			const proxy = new Contract(
				deployments.testnet.marketContractId,
				proxyAbi,
				provider,
			);
			const market = new Contract(
				deployments.testnet.marketContractId,
				marketV2Abi,
				provider,
			);
			const implementation = new Contract(
				MARKET_V2.marketImplementationContractId,
				marketV2Abi,
				provider,
			);
			const [managerResult, proxyOwnerResult, fuel, stFuel, reserve, fees] =
				await Promise.all([
					vault.functions.manager().get(),
					proxy.functions.proxy_owner().get(),
					vault.functions
						.asset_status({ bits: deployments.testnet.fuelAssetId })
						.get(),
					vault.functions
						.asset_status({ bits: deployments.testnet.stFuelAssetId })
						.get(),
					vault.functions.reserve_status().get(),
					market.functions
						.fee_balance({ bits: feeAssetId })
						.addContracts([implementation])
						.get(),
				]);
			setManager(identity(managerResult.value));
			setProxyOwner(identity(proxyOwnerResult.value));
			setBalances({
				reserve: decimalAmount(BigInt(reserve.value[0].toString()), 6),
				reserveObligation: decimalAmount(BigInt(reserve.value[1].toString()), 6),
				reserveFree: decimalAmount(BigInt(reserve.value[2].toString()), 6),
				fuelPrincipal: decimalAmount(BigInt(fuel.value[1].toString()), 9),
				fuelDeployed: decimalAmount(BigInt(fuel.value[2].toString()), 9),
				fuelPenalties: decimalAmount(BigInt(fuel.value[3].toString()), 9),
				fuelFree: decimalAmount(BigInt(fuel.value[4].toString()), 9),
				stFuelPrincipal: decimalAmount(BigInt(stFuel.value[1].toString()), 9),
				stFuelDeployed: decimalAmount(BigInt(stFuel.value[2].toString()), 9),
				stFuelPenalties: decimalAmount(BigInt(stFuel.value[3].toString()), 9),
				stFuelFree: decimalAmount(BigInt(stFuel.value[4].toString()), 9),
				fee: decimalAmount(
					BigInt(fees.value.toString()),
					feeAsset === "USDC" ? 6 : 9,
				),
			});
		} catch (reason) {
			setStatus(actionError(reason));
		}
	}
	useEffect(() => {
		void refresh();
	}, [wallet, feeAsset]);

	async function execute(
		label: string,
		send: () => Promise<{
			waitForResult(): Promise<{ transactionId: string }>;
		}>,
	) {
		if (!wallet || busy) return;
		setBusy(true);
		setStatus(`${label}…`);
		setTransactionId("");
		try {
			const result = await (await send()).waitForResult();
			telemetry("transaction", "success");
			setStatus(`${label} complete.`);
			setTransactionId(result.transactionId);
			await refresh();
		} catch (reason) {
			telemetry("transaction", "failure");
			setStatus(actionError(reason));
		} finally {
			setBusy(false);
		}
	}
	function contracts() {
		if (!wallet) throw new Error("Connect the owner wallet first.");
		return {
			vault: new Contract(
				deployments.testnet.vaultContractId,
				vaultAbi,
				wallet,
			),
			market: new Contract(
				deployments.testnet.marketContractId,
				marketV2Abi,
				wallet,
			),
			implementation: new Contract(
				MARKET_V2.marketImplementationContractId,
				marketV2Abi,
				wallet,
			),
			proxy: new Contract(
				deployments.testnet.marketContractId,
				proxyAbi,
				wallet,
			),
		};
	}
	function raw(value: string, decimals: number) {
		const amount = decimalRawAmount(value, decimals);
		if (amount <= 0n || amount > 18_446_744_073_709_551_615n)
			throw new Error("Enter a valid positive amount.");
		return amount;
	}
	function address(value: string) {
		if (!validAddress(value)) throw new Error("Enter a 32-byte 0x address.");
		return { Address: { bits: value } };
	}
	const ownerDisabled = busy || !isManager;
	return (
		<Page
			eyebrow="RESTRICTED OPERATIONS"
			title="Vault administration"
			description="Wallet-signed owner operations. The contracts enforce every permission on-chain."
		>
			{!account ? (
				<button
					className="primary admin-connect"
					onClick={() => void connectTracked(connect)}
				>
					Connect owner wallet
				</button>
			) : (
				<>
					<div className="admin-summary panel">
						<div>
							<span>Connected</span>
							<strong>{short(account)}</strong>
						</div>
						<div>
							<span>Vault manager</span>
							<strong>{short(manager) || "Unavailable"}</strong>
						</div>
						<div>
							<span>Proxy owner</span>
							<strong>{short(proxyOwner) || "Unavailable"}</strong>
						</div>
					</div>
					<div className="admin-grid">
						<section className="panel admin-card">
							<h2>Reserve coverage</h2>
							<p className="big-number">{balances.reserve} USDC</p>
							<p>
								Protected: {balances.reserveObligation} · Free: {balances.reserveFree}
							</p>
							<label>
								USDC amount
								<input
									inputMode="decimal"
									value={reserveAmount}
									onChange={(event) => setReserveAmount(event.target.value)}
								/>
							</label>
							<button
								className="primary"
								disabled={ownerDisabled}
								onClick={() =>
									void execute("Funding reserve", () =>
										contracts()
											.vault.functions.fund_reserve()
											.callParams({
												forward: [
													raw(reserveAmount, 6).toString(),
													deployments.testnet.usdcAssetId,
												],
											})
											.call(),
									)
								}
							>
								Fund reserve
							</button>
						</section>
						<section className="panel admin-card">
							<h2>Withdraw surplus</h2>
							<p>
								Available: {withdrawAsset === "USDC" ? balances.reserveFree : withdrawAsset === "FUEL" ? balances.fuelFree : balances.stFuelFree} {withdrawAsset}
							</p>
							<label>
								Asset
								<select
									value={withdrawAsset}
									onChange={(event) => setWithdrawAsset(event.target.value as Asset | "USDC")}
								>
									<option>USDC</option>
									<option>FUEL</option>
									<option>stFUEL</option>
								</select>
							</label>
							<label>
								Amount
								<input
									inputMode="decimal"
									value={withdrawAmount}
									onChange={(event) => setWithdrawAmount(event.target.value)}
								/>
							</label>
							<button
								className="primary"
								disabled={ownerDisabled}
								onClick={() =>
									void execute("Withdrawing surplus", () =>
										contracts()
											.vault.functions.withdraw_surplus(
												{ bits: withdrawAssetId },
												raw(withdrawAmount, withdrawAsset === "USDC" ? 6 : 9).toString(),
											)
											.txParams({ variableOutputs: 1 })
											.call(),
									)
								}
							>
								Withdraw to manager
							</button>
						</section>
						<section className="panel admin-card">
							<h2>Strategy allowlist</h2>
							<label>
								Strategy address
								<input
									value={strategy}
									onChange={(event) => setStrategy(event.target.value.trim())}
									placeholder="0x…"
								/>
							</label>
							<div className="button-row">
								<button
									disabled={ownerDisabled}
									onClick={() =>
										void execute("Allowlisting strategy", () =>
											contracts()
												.vault.functions.set_strategy(address(strategy), true)
												.call(),
										)
									}
								>
									Allow
								</button>
								<button
									disabled={ownerDisabled}
									onClick={() =>
										void execute("Removing strategy", () =>
											contracts()
												.vault.functions.set_strategy(address(strategy), false)
												.call(),
										)
									}
								>
									Revoke
								</button>
							</div>
						</section>
						<section className="panel admin-card">
							<h2>Strategy funds</h2>
							<p>
								Protected / deployed / penalties / free: {balances.fuelPrincipal} /{" "}
								{balances.fuelDeployed} / {balances.fuelPenalties} / {balances.fuelFree} FUEL ·{" "}
								{balances.stFuelPrincipal} / {balances.stFuelDeployed} / {balances.stFuelPenalties} / {balances.stFuelFree} stFUEL
							</p>
							<label>
								Asset
								<select
									value={asset}
									onChange={(event) => setAsset(event.target.value as Asset)}
								>
									<option>FUEL</option>
									<option>stFUEL</option>
								</select>
							</label>
							<label>
								Amount
								<input
									inputMode="decimal"
									max={asset === "FUEL" ? balances.fuelFree : balances.stFuelFree}
									value={amount}
									onChange={(event) => setAmount(event.target.value)}
								/>
							</label>
							<div className="button-row">
								<button
									className="primary"
									disabled={ownerDisabled}
									onClick={() =>
										void execute("Deploying strategy funds", () =>
											contracts()
												.vault.functions.deploy_to_strategy(
													address(strategy),
													{ bits: assetId },
													raw(amount, 9).toString(),
												)
												.txParams({ variableOutputs: 1 })
												.call(),
										)
									}
								>
									Deploy
								</button>
								<button
									disabled={ownerDisabled}
									onClick={() =>
										void execute("Returning strategy funds", () =>
											contracts()
												.vault.functions.return_from_strategy(address(strategy))
												.callParams({
													forward: [raw(amount, 9).toString(), assetId],
												})
												.call(),
										)
									}
								>
									Return
								</button>
							</div>
							<small>
								Return sends funds already held by the connected manager wallet
								back to the Vault.
							</small>
						</section>
						<section className="panel admin-card">
							<h2>Vault controls</h2>
							<div className="button-row">
								<button
									disabled={ownerDisabled}
									onClick={() =>
										void execute("Pausing Vault", () =>
											contracts().vault.functions.set_paused(true).call(),
										)
									}
								>
									Pause
								</button>
								<button
									disabled={ownerDisabled}
									onClick={() =>
										void execute("Resuming Vault", () =>
											contracts().vault.functions.set_paused(false).call(),
										)
									}
								>
									Resume
								</button>
							</div>
							<label>
								Next manager
								<input
									value={nextManager}
									onChange={(event) =>
										setNextManager(event.target.value.trim())
									}
									placeholder="0x…"
								/>
							</label>
							<div className="button-row">
								<button
									disabled={ownerDisabled}
									onClick={() =>
										void execute("Proposing manager", () =>
											contracts()
												.vault.functions.propose_manager(address(nextManager))
												.call(),
										)
									}
								>
									Propose manager
								</button>
								<button
									disabled={busy}
									onClick={() =>
										void execute("Accepting manager role", () =>
											contracts().vault.functions.accept_manager().call(),
										)
									}
								>
									Accept manager
								</button>
							</div>
						</section>
						<section className="panel admin-card">
							<h2>Market controls</h2>
							<div className="button-row">
								<button
									disabled={busy}
									onClick={() =>
										void execute("Pausing market", () => {
											const { market, implementation } = contracts();
											return market.functions
												.set_paused(true)
												.addContracts([implementation])
												.call();
										})
									}
								>
									Pause
								</button>
								<button
									disabled={busy}
									onClick={() =>
										void execute("Resuming market", () => {
											const { market, implementation } = contracts();
											return market.functions
												.set_paused(false)
												.addContracts([implementation])
												.call();
										})
									}
								>
									Resume
								</button>
							</div>
							<label>
								Fee basis points
								<input
									inputMode="numeric"
									value={feeBps}
									onChange={(event) => setFeeBps(event.target.value)}
								/>
							</label>
							<button
								disabled={busy}
								onClick={() =>
									void execute("Updating market fee", () => {
										const fee = Number(feeBps);
										if (!Number.isSafeInteger(fee) || fee < 0 || fee > 1_000)
											throw new Error("Fee must be 0–1000 bps.");
										const { market, implementation } = contracts();
										return market.functions
											.set_fee_bps(fee)
											.addContracts([implementation])
											.call();
									})
								}
							>
								Set fee
							</button>
							<label>
								Fee asset
								<select
									value={feeAsset}
									onChange={(event) =>
										setFeeAsset(event.target.value as "ETH" | "USDC")
									}
								>
									<option>USDC</option>
									<option>ETH</option>
								</select>
							</label>
							<p>
								Accrued: {balances.fee} {feeAsset}
							</p>
							<button
								disabled={busy}
								onClick={() =>
									void execute("Withdrawing fees", () => {
										const { market, implementation } = contracts();
										return market.functions
											.withdraw_fees({ bits: feeAssetId })
											.addContracts([implementation])
											.txParams({ variableOutputs: 1 })
											.call();
									})
								}
							>
								Withdraw fees
							</button>
							<label>
								Next market owner
								<input
									value={nextMarketOwner}
									onChange={(event) =>
										setNextMarketOwner(event.target.value.trim())
									}
									placeholder="0x…"
								/>
							</label>
							<div className="button-row">
								<button
									disabled={busy}
									onClick={() =>
										void execute("Proposing market owner", () => {
											const { market, implementation } = contracts();
											return market.functions
												.propose_owner(address(nextMarketOwner))
												.addContracts([implementation])
												.call();
										})
									}
								>
									Propose owner
								</button>
								<button
									disabled={busy}
									onClick={() =>
										void execute("Accepting market ownership", () => {
											const { market, implementation } = contracts();
											return market.functions
												.accept_owner()
												.addContracts([implementation])
												.call();
										})
									}
								>
									Accept owner
								</button>
							</div>
							<small>
								The market contract performs the definitive owner check.
							</small>
						</section>
						<section className="panel admin-card">
							<h2>Proxy ownership</h2>
							<label>
								Next proxy owner
								<input
									value={nextProxyOwner}
									onChange={(event) =>
										setNextProxyOwner(event.target.value.trim())
									}
									placeholder="0x…"
								/>
							</label>
							<div className="button-row">
								<button
									disabled={busy || !isProxyOwner}
									onClick={() =>
										void execute("Proposing proxy owner", () =>
											contracts()
												.proxy.functions.propose_proxy_owner(
													address(nextProxyOwner),
												)
												.call(),
										)
									}
								>
									Propose proxy owner
								</button>
								<button
									disabled={busy}
									onClick={() =>
										void execute("Accepting proxy ownership", () =>
											contracts().proxy.functions.accept_proxy_owner().call(),
										)
									}
								>
									Accept proxy owner
								</button>
							</div>
						</section>
					</div>
					{status && (
						<p className="status-message" role="status">
							{status}
							{transactionId && (
								<>
									{" "}
									<a
										href={`https://app-testnet.fuel.network/tx/${transactionId}`}
										target="_blank"
										rel="noopener noreferrer"
									>
										View transaction ↗
									</a>
								</>
							)}
						</p>
					)}
					{!isManager && (
						<p className="fine wide">
							This wallet is not the current Vault manager. Manager-only Vault
							controls are disabled; pending-role acceptance remains available.
						</p>
					)}
				</>
			)}
		</Page>
	);
}

function Page({
	eyebrow,
	tag,
	title,
	description,
	children,
}: {
	eyebrow: string;
	tag?: string;
	title: string;
	description: string;
	children: React.ReactNode;
}) {
	return (
		<main className="page-panel">
			<section className="page-head">
				{tag && <span className="page-tag">{tag}</span>}
				{eyebrow && <p className="eyebrow">{eyebrow}</p>}
				<h1>{title}</h1>
				<p>{description}</p>
			</section>
			{children}
		</main>
	);
}
function Empty({ title, text }: { title: string; text: string }) {
	return (
		<div className="empty">
			<span>◇</span>
			<h2>{title}</h2>
			<p>{text}</p>
		</div>
	);
}
function Stat({
	label,
	mobileLabel,
	value,
}: {
	label: string;
	mobileLabel?: string;
	value: string;
}) {
	return (
		<article className="stat">
			<span className={mobileLabel ? "desktop-stat-label" : undefined}>
				{label}
			</span>
			{mobileLabel && <span className="mobile-stat-label">{mobileLabel}</span>}
			<strong>{value}</strong>
		</article>
	);
}
function Metric({ label, value }: { label: string; value: string }) {
	return (
		<div className="metric">
			<span>{label}</span>
			<strong>{value}</strong>
		</div>
	);
}

export default function App() {
	const page = location.pathname;
	return (
		<>
			<Telemetry />
			<Header />
			{page === "/" ? (
				<Earn />
			) : page === "/positions" ? (
				<Positions />
			) : page === "/market" ? (
				<Market />
			) : page === "/stats" ? (
				<StatsPage />
			) : page === "/faucet" ? (
				<Faucet />
			) : page === "/docs" ? (
				<Docs />
			) : page === "/admin" ? (
				<Admin />
			) : (
				<Page
					eyebrow="404"
					title="Page not found"
					description="The requested page does not exist."
				>
					<a className="primary link-button" href="/">
						Return home
					</a>
				</Page>
			)}
		</>
	);
}
